import { Alert, Box, Chip, Typography, Grid, Paper, IconButton, CircularProgress, Dialog, Button, DialogContent, DialogTitle, DialogActions, LinearProgress, Tooltip, Skeleton, GlobalStyles, Slider, Collapse, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Select, MenuItem, InputLabel, TextField, InputBase } from '@mui/material';
import { useState, useEffect, useCallback, useRef, useMemo, Fragment} from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
// import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmber';
// import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Videocam, Edit, Cancel, Redo, ArrowBack, Refresh, InfoOutlined,  AttachFile, PhotoLibrary, WebAsset, ZoomIn, ZoomOut, Fullscreen, FullscreenExit,ExpandMore, Search, KeyboardArrowUp, KeyboardArrowDown, Close, PanTool  } from '@mui/icons-material';
import {
  detectionOutcome,
  isManuallyResolved,
  canManuallyResolve,
  DETECTION_OUTCOME_LABEL,
  DETECTION_OUTCOME_SEVERITY,
  formatModelName,
} from 'utils/detectionOutcome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import video from 'assets/images/icons/video.png';
import useApi from 'hooks/useApi';
import useSelection from 'hooks/useSelection';
import { useOutgoingCall } from 'contexts/OutgoingCallContext';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FormData from './formData';
import AddTransactionPage from './AddTrasaction/AddTransaction';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MainCard from 'components/MainCard';
import { PERMISSIONS } from 'contexts/PermissionContext';
import { useHasPermission } from 'contexts/PermissionContext';
import EditForm from './editForm';
import MissedCallDetailsDrawer from './Callhistory';
import {  isValidDateValue, formatDateWithTimezoneDisplay, formatTimestampWithLocalTimezone } from 'utils/dateFormat';
import TimestampDisplay from 'components/TimestampDisplay';
import PdfThumbnail from 'components/pdf/PdfThumbnail';
import ResourceDetailsDialog from 'components/ResourceRating/ResourceDetailsDialog';
import LiveTrackingMap from './TrackingMap';
import ProgressTrackerStepper from './details/ProgressTrackerStepper';
import LiveDriverPanel from './details/LiveDriverPanel';
import AssignedDriverPanel from './details/AssignedDriverPanel';
import JumioVerificationBadge, { JumioVerificationMap, verificationFor } from 'components/JumioVerificationBadge';
import DriverActionsPanel from './details/DriverActionsPanel';
import AiIssuesSummary from './details/AiIssuesSummary';
import NextStepsPanel, { NextStep } from './details/NextStepsPanel';
import { useChatNotificationContext } from 'contexts/ChatNotificationContext';
import useFirebase from 'hooks/useFirebase';
import { limitToLast, onChildAdded, query, ref } from 'firebase/database';
// import ChildTransactionsDialog from './ChildTransaction';
import ChildDetailsDialog from './ChildDetailsDialog';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import InlineChildTransactionForm from './InlineChildTransactionForm';

type TransactionDetailsEvent = {
  action: 'create' | 'update';
  teamId: string;
  teamUid?: string;
  clientId?: string;
  clientUid?: string;
  keyId?: string;
  keyUid?: string;
  source: string;
  occurredAt: string;
  meta?: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isTransactionDetailsEvent = (value: unknown): value is TransactionDetailsEvent => {
  if (!isRecord(value)) return false;
  if (value.action !== 'create' && value.action !== 'update') return false;
  if (typeof value.teamId !== 'string' || value.teamId.trim().length === 0) return false;
  if (typeof value.source !== 'string' || value.source.trim().length === 0) return false;
  if (typeof value.occurredAt !== 'string' || value.occurredAt.trim().length === 0) return false;
  if (value.teamUid !== undefined && typeof value.teamUid !== 'string') return false;
  if (value.clientId !== undefined && typeof value.clientId !== 'string') return false;
  if (value.clientUid !== undefined && typeof value.clientUid !== 'string') return false;
  if (value.keyId !== undefined && typeof value.keyId !== 'string') return false;
  if (value.keyUid !== undefined && typeof value.keyUid !== 'string') return false;
  if (value.meta !== undefined && !isRecord(value.meta)) return false;
  return true;
};

type FieldHistoryEntry = {
  editedAt?: string;
  editedBy?: any;
  change?: { inputComponent?: string; previousValue?: any };
};

function formatPreviousValue(prev: any, inputComp: string): string {
  if (!prev) return 'NA';
  const v = prev?.value;
  const files = Array.isArray(prev?.files) ? prev.files : [];
  if (files.length && (inputComp === 'CAMERA' || inputComp === 'ESIGN' || inputComp === 'SWITCH')) {
    return files.map((f: any) => f?.file_name || 'file').join(', ');
  }
  if (v === undefined || v === null || v === '') return 'NA';

  // RESOURCE — value is an array of resource objects { id, fields, category, ... }.
  // Render the first 2 scalar field values per resource so we don't fall through
  // to the generic Array path which would `.join` objects → "[object Object]".
  if (inputComp === 'RESOURCE') {
    const list = Array.isArray(v) ? v : [v];
    const formatted = list
      .map((r: any) => {
        if (!r || typeof r !== 'object') return String(r ?? '');
        const fields = r.fields;
        if (fields && typeof fields === 'object' && Object.keys(fields).length > 0) {
          const scalars = Object.values(fields).filter(
            (val) => val !== null && val !== undefined && val !== '' && typeof val !== 'object'
          );
          if (scalars.length > 0) {
            return scalars.slice(0, 2).map((val) => String(val)).join(' ');
          }
        }
        return String(r.id ?? '');
      })
      .filter((s) => s.length > 0)
      .join(' | ');
    return formatted || 'NA';
  }

  if (Array.isArray(v)) {
    // Detect date-shaped values by shape, not inputComp: a dynamic SELECT/RADIO
    // sourced from a DATE field carries date objects while reporting as SELECT.
    const result = v
      .map((x: any) =>
        isValidDateValue(x)
          ? formatDateWithTimezoneDisplay(x)
          : x && typeof x === 'object'
          ? JSON.stringify(x)
          : x,
      )
      .filter((x: any) => x !== null && x !== undefined && x !== '')
      .join(', ');
    return result || 'NA';
  }
  if (inputComp === 'DATE') return formatDateWithTimezoneDisplay(v);
  return String(v);
}

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);

function FieldPreviousValuesAccordion({
  history,
  currentInputComponent,
  fieldKey,
  onOpenImages,
  onOpenVideo,
}: {
  history: FieldHistoryEntry[];
  currentInputComponent: string;
  fieldKey: string;
  onOpenImages?: (images: { src: string }[], index: number) => void;
  onOpenVideo?: (url: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!history.length) return null;
  return (
    <Box
      sx={{
        mx: '5px',
        mt: '2px',
        mb: '4px',
        minHeight: 28,
        borderRadius: 0.5,
        bgcolor: '#fff7e6',
        borderLeft: '2px solid #f5a623',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((p) => !p);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.75,
          py: 0.4,
          minHeight: 28,
          boxSizing: 'border-box',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: '#ffeec9' },
        }}
      >
        <Typography
          sx={{
            fontSize: '0.62rem',
            fontWeight: 700,
            color: '#b46900',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          Previous Value{history.length > 1 ? `s (${history.length})` : ''}
        </Typography>
        <ExpandMore
          sx={{
            fontSize: '1rem',
            color: '#b46900',
            transition: 'transform 0.2s ease-in-out',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 0.75, pb: 0.5 }}>
          {history.map((h, i) => {
            const inputComp = h.change?.inputComponent || currentInputComponent;
            const prev = h.change?.previousValue;
            const files: any[] = Array.isArray(prev?.files) ? prev.files : [];
            return (
              <Box key={`prev-${fieldKey}-${i}`} sx={{ mt: i === 0 ? 0 : 0.5 }}>
                {files.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                    {files.map((file: any, fi: number) => {
                      const ext = file.file_name?.split('.').pop()?.toLowerCase() || '';
                      const isImage = IMAGE_EXTS.has(ext);
                      const isVideo = ext === 'mp4';
                      const fileUrl = file.signedUrl || file.url || file.public_url;
                      return (
                        <Box
                          key={`prev-file-${fieldKey}-${i}-${fi}`}
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: '1.5px solid',
                            borderColor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isImage ? 'transparent' : 'grey.50',
                            p: '4px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { borderColor: 'primary.main', transform: 'scale(1.05)' },
                          }}
                        >
                          {fileUrl ? (
                            isImage || isVideo ? (
                              <img
                                src={isVideo ? video : fileUrl}
                                alt={file.file_name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isVideo) {
                                    onOpenVideo?.(fileUrl);
                                  } else {
                                    const imgs = files
                                      .filter((f: any) => IMAGE_EXTS.has(f.file_name?.split('.').pop()?.toLowerCase() || ''))
                                      .map((f: any) => ({ src: f.signedUrl || f.url || f.public_url }));
                                    const idx = imgs.findIndex((f: any) => f.src === fileUrl);
                                    onOpenImages?.(imgs, idx >= 0 ? idx : 0);
                                  }
                                }}
                              />
                            ) : (
                              <Tooltip title={file.file_name || 'Document'} arrow>
                                <Box
                                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', textAlign: 'center' }}
                                  onClick={(e) => { e.stopPropagation(); window.open(fileUrl, '_blank'); }}
                                >
                                  <InsertDriveFileIcon sx={{ fontSize: 20, color: 'primary.main', mb: '3px' }} />
                                  <Typography sx={{ fontSize: '6px', color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', mb: '2px' }}>
                                    {ext || 'FILE'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '10px', color: 'text.secondary', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', px: '2px' }}>
                                    {file.file_name || 'Document'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatPreviousValue(prev, inputComp)}
                  </Typography>
                )}
                {h.editedAt && (
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                    {formatTimestampWithLocalTimezone(h.editedAt)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function TransactionDetails() {
  // Navigation hook
  const navigate = useNavigate();
  
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };
  // MissedCallDetailsDrawer needs signed videoSession recording URLs, which
  // only the full combined fetch produces (core/summary/timeline are all
  // lighter than that on purpose) — fetched on demand when the drawer opens,
  // same on-demand pattern as ChildDetailsDialog.
  const [missedCallHistory, setMissedCallHistory] = useState<any[]>([]);

  // Chat notification context for auto-opening chat from notifications
  const { pendingChatNotification } = useChatNotificationContext();

  // Permission check for editing transactions
  const canEditTransaction = useHasPermission(PERMISSIONS.EDIT_TRANSACTION);
  const canViewTransactionEmbed = useHasPermission(PERMISSIONS.TRANSACTION_EMBED_ACCESS);
  const hasDraggedRef = useRef(false);
  // Auto-open drawer when there's a pending chat notification
  useEffect(() => {
    if (pendingChatNotification?.threadUid) {
      setOpen(true);
    }
  }, [pendingChatNotification]);

  // Handle browser back button
  useEffect(() => {
    // Add a state to the history when component mounts
    const currentState = { page: 'transaction-details' };
    window.history.replaceState(currentState, '', window.location.href);
    
    const handlePopState = (event: PopStateEvent) => {
      // Always redirect to transactions page on back button
      navigate('/transactions', { replace: true });
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  

  const closeViewer = () => {
    setViewerIsOpen(false);
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
    panRef.current = { x: 0, y: 0 };
    zoomLevelRef.current = 1;
    isPanningRef.current = false;
    setIsDragging(false);
    setIsMaximized(false); // ← add this
  };

  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const lastMouse = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const zoomLevelRef = useRef(zoomLevel);
  const panRef = useRef({ x: 0, y: 0 });

// Keep refs in sync with state
useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);
  ;

  // Reset pan when image changes
  const [showVideo, setShowVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedDetections, setExpandedDetections] = useState<Set<string>>(new Set());
  const [transactionDetails, setTransactionDetails] = useState<any>([]);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  // teamUid comes straight from the route (transactions/details/:teamUid/...)
  // — available on the very first render, unlike transactionDetails.team,
  // which only exists after core resolves. Anything that only needs the
  // team id (detection-config check, flow status) should use this, not the
  // core-derived fallback chain, so it can fire immediately instead of
  // waiting on core to round-trip first.
  const { teamUid: routeTeamUid, transUid } = useParams();
  const [isMaximized, setIsMaximized] = useState(false);
useEffect(() => {
  if (!viewerIsOpen) return;

  let cleanup: (() => void) | null = null;

  const attachListeners = () => {
    const backdrop = document.getElementById('viewerBackdrop');
    if (!backdrop) return false;

    const onMouseDown = (e: MouseEvent) => {
      if (zoomLevelRef.current <= 1) return;
      isPanningRef.current = true;
      hasDraggedRef.current = false;
      setIsDragging(true);
      lastMouse.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      
      // Only mark as dragged if moved more than 3px (avoids micro-movements)
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        hasDraggedRef.current = true;
      }
      
      lastMouse.current = { x: e.clientX, y: e.clientY };
      panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy };
      setPan({ ...panRef.current });
    };

    const onMouseUp = () => {
      isPanningRef.current = false;
      setIsDragging(false);
    };

    const onClickCapture = (e: MouseEvent) => {
      if (hasDraggedRef.current) {
        e.stopPropagation();  // prevents backdropCloseable from firing
        hasDraggedRef.current = false;
      }
  };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      setZoomLevel(prev => {
        const next = Math.min(4, Math.max(1, prev + delta));
        if (next <= 1) {
          panRef.current = { x: 0, y: 0 };
          setPan({ x: 0, y: 0 });
        }
        zoomLevelRef.current = next;
        return next;
      });
    };

    backdrop.addEventListener('mousedown', onMouseDown);
    backdrop.addEventListener('wheel', onWheel, { passive: false });
    backdrop.addEventListener('click', onClickCapture, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    cleanup = () => {
      backdrop.removeEventListener('mousedown', onMouseDown);
      backdrop.removeEventListener('wheel', onWheel);
      backdrop.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    return true;
  };

  // Try immediately first
  if (!attachListeners()) {
    // Element not ready yet — use MutationObserver to wait for it
    const observer = new MutationObserver(() => {
      if (attachListeners()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      cleanup?.();
    };
  }

  return () => cleanup?.();
}, [viewerIsOpen]);

useEffect(() => {
  if (!viewerIsOpen) return;
  const backdrop = document.getElementById('viewerBackdrop');
  if (!backdrop) return;

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setZoomLevel(prev => {
      const next = Math.min(4, Math.max(1, prev + delta));
      if (next <= 1) {
        panRef.current = { x: 0, y: 0 };
        setPan({ x: 0, y: 0 });
      }
      zoomLevelRef.current = next;
      return next;
    });
  };

  backdrop.addEventListener('wheel', onWheel, { passive: false });
  return () => backdrop.removeEventListener('wheel', onWheel);
}, [viewerIsOpen]);



  console.log(transUid,"transUid......");
  const [imagesPreview, setImagesPreview] = useState<any>([]);
  const gotoNext = () => {
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
    setCurrentImage((prev) => (prev + 1) % imagesPreview.length);
  };
  const gotoPrev = () => {
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
    setCurrentImage((prev) => (prev - 1 + imagesPreview.length) % imagesPreview.length);
  };

  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [resourceDialogData, setResourceDialogData] = useState<any>(null);
  // const [openChildDialog, setOpenChildDialog] = useState(false);

  // const handleOpenChildDialog = () => setOpenChildDialog(true);
  // const handleCloseChildDialog = () => setOpenChildDialog(false);
  
  // Function to handle dialog close and reset tab
  const handleResourceDialogClose = () => {
    setResourceDialogOpen(false);
  };
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [editingStepItem, setEditingStepItem] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [formRefreshLoading, setFormRefreshLoading] = useState(false);
  const [redoConfirmOpen, setRedoConfirmOpen] = useState(false);
  const [stepToRedo, setStepToRedo] = useState<any>(null);
  const [redoType, setRedoType] = useState<'current' | 'full' | 'role'>('current');
  const [selectedRoleUid, setSelectedRoleUid] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  //adding loading state to prevent duplicate API calls
  const  [isInitialLoad, setIsInitialLoad]  = useState(true);

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<any>(null);
  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    getTransactionHistoryCore,
    getTransactionHistorySummary,
    getTransactionHistoryTimeline,
    getTransactionHistoryDetails,
    updateTransactionHistoryStep,
    redoStep, retryAiDetection, resolveDetectionResult, clearDetectionResolution,
    resetToStep, resetToStepByRole, getResourceTypes, user,
    showToast,
    getTransactionAttachments,
    getTransactionEmbedUrl,
    transactionKanban,
    getVerificationStatusBatch
  } = useApi();
  const { selectedClient, dateRange: selectionDateRange } = useSelection();

  // Super Admin (matches the backend `!role.client` convention; the sidebar shows
  // this as the role name). Gates the Retry-detection + Stop-tracking admin
  // actions on this page. The backend enforces it regardless.
  const _superAdminRole = (
    (user as any)?.role?.name ?? (user as any)?.role?.roleName ?? (user as any)?.role?.roleType ?? ''
  ).toString().toLowerCase().trim();
  const isSuperAdmin =
    _superAdminRole === 'super admin' || _superAdminRole === 'superadmin' || _superAdminRole === 'sys_admin';
  // Per-detection retry: `retryingDetection` is keyed by `${eventId}:${configId}`
  // so only the clicked detection spins. `confirmRetry` drives the confirm dialog.
  const [retryingDetection, setRetryingDetection] = useState<string | null>(null);
  const [confirmRetry, setConfirmRetry] = useState<{ eventId: string; configId: string; name: string } | null>(null);
  async function runRetryDetection(eventId: string, configId: string) {
    if (!eventId || !configId || !retryAiDetection) return;
    const key = `${eventId}:${configId}`;
    setRetryingDetection(key);
    try {
      const res: any = await retryAiDetection(eventId, configId);
      if (res?.success === false) {
        showToast?.(typeof res?.message === 'string' ? res.message : 'Failed to re-run detection', 'error');
      } else {
        showToast?.('AI detection re-run', 'success');
        const uid = (transactionDetails as any)?.uid;
        if (uid) await refreshAfterEdit(uid);
      }
    } catch (e: any) {
      showToast?.(e?.message || 'Failed to re-run detection', 'error');
    } finally {
      setRetryingDetection(null);
    }
  }

  // ── Manual resolution of INCONCLUSIVE detection results ───────────────────
  // Only `type === 'other'` can be resolved: inconclusive is the system saying
  // "I couldn't tell, a human should look", so resolving it finishes that
  // workflow. A confidently-wrong verdict is a rule defect, not something to
  // overwrite here — the backend enforces the same rule.
  const [resolveDialog, setResolveDialog] = useState<{
    eventId: string;
    configId: string;
    name: string;
    aiMessage?: string;
    outcome: 'true' | 'false';
    reason: string;
  } | null>(null);
  const [resolvingDetection, setResolvingDetection] = useState<string | null>(null);

  async function submitResolveDetection() {
    const d = resolveDialog;
    if (!d || !resolveDetectionResult) return;
    if (!d.reason.trim()) {
      showToast?.('A reason is required', 'error');
      return;
    }
    const key = `${d.eventId}:${d.configId}`;
    setResolvingDetection(key);
    try {
      const res: any = await resolveDetectionResult(d.eventId, d.configId, d.outcome, d.reason.trim());
      if (res?.success === false) {
        showToast?.(typeof res?.message === 'string' ? res.message : 'Could not resolve the result', 'error');
      } else {
        showToast?.('Result resolved', 'success');
        setResolveDialog(null);
        const uid = (transactionDetails as any)?.uid;
        if (uid) await refreshAfterEdit(uid);
      }
    } catch (e: any) {
      showToast?.(e?.response?.data?.message || e?.message || 'Could not resolve the result', 'error');
    } finally {
      setResolvingDetection(null);
    }
  }

  async function undoResolveDetection(eventId: string, configId: string) {
    if (!eventId || !configId || !clearDetectionResolution) return;
    const key = `${eventId}:${configId}`;
    setResolvingDetection(key);
    try {
      const res: any = await clearDetectionResolution(eventId, configId);
      if (res?.success === false) {
        showToast?.(typeof res?.message === 'string' ? res.message : 'Could not clear the resolution', 'error');
      } else {
        showToast?.('Resolution cleared', 'success');
        const uid = (transactionDetails as any)?.uid;
        if (uid) await refreshAfterEdit(uid);
      }
    } catch (e: any) {
      showToast?.(e?.response?.data?.message || e?.message || 'Could not clear the resolution', 'error');
    } finally {
      setResolvingDetection(null);
    }
  }
  // Paginated event-card timeline (separate from transactionDetails, which now
  // only carries core + summary tier fields). timelineEvents is newest-first,
  // accumulated as the user scrolls.
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const [timelineCursor, setTimelineCursor] = useState<string | null>(null);
  const [newestLoadedAt, setNewestLoadedAt] = useState<string | null>(null);
  const [hasMoreTimeline, setHasMoreTimeline] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [timelineLoadingMore, setTimelineLoadingMore] = useState(false);
  // In-flight guard for loadTimelineMore. Must be a ref, not the state above:
  // scroll fires many times per tick and setState doesn't commit until the next
  // render, so a state guard lets every one of those handlers through with the
  // same cursor and each appends the same page. The state is kept only to drive
  // the spinner. Same reason isLoadingRef/realtimeInFlightRef are refs.
  const timelineMoreRef = useRef(false);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Jumio identity-verification state for every person named on this page.
  //
  // Looked up LIVE rather than read off the event snapshot: a RESOURCE field
  // stores the resource's fields as they were when the step was submitted, so a
  // driver who verified afterwards would show as unverified forever. Keyed by
  // ResourceData uid (the `id` the snapshot carries) and by _id.
  const [verificationMap, setVerificationMap] = useState<JumioVerificationMap>({});

  // Every resource id referenced by a RESOURCE field anywhere in the history.
  // Not filtered to user-type resources — the backend answers `applicable:false`
  // for anything with no licence field, and the badge renders nothing for those.
  const verificationUidsKey = useMemo(() => {
    const ids = new Set<string>();
    (Array.isArray(transactionDetails?.history) ? transactionDetails.history : []).forEach((item: any) => {
      // The person who submitted / last updated the step, shown in the card header.
      // `user` is populated with uid; `updatedBy` only with names, so fall back to _id
      // (the batch response is keyed by both).
      [item?.user, item?.updatedBy].forEach((u: any) => {
        if (!u || typeof u !== 'object') return;
        const uid = u.uid || u._id || u.id;
        if (uid) ids.add(String(uid));
      });
      (Array.isArray(item?.data) ? item.data : []).forEach((f: any) => {
        if (f?.inputComponent !== 'RESOURCE') return;
        const vals = f?.value?.value;
        const arr = Array.isArray(vals) ? vals : vals ? [vals] : [];
        arr.forEach((r: any) => {
          if (!r || typeof r !== 'object') return;
          const rid = r.resourceDataId || r.id || r._id || r.uid;
          if (rid) ids.add(String(rid));
        });
      });
    });
    return Array.from(ids).sort().join(',');
  }, [transactionDetails?.history]);

  // Held in a ref, NOT an effect dependency: ApiContext rebuilds its value object
  // on every render, so depending on the function directly would refire this
  // lookup on every unrelated provider render.
  const verificationFetchRef = useRef(getVerificationStatusBatch);
  verificationFetchRef.current = getVerificationStatusBatch;

  useEffect(() => {
    if (!verificationUidsKey) {
      setVerificationMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await verificationFetchRef.current?.(verificationUidsKey.split(','));
        const data = res?.data ?? {};
        if (!cancelled) setVerificationMap(data && typeof data === 'object' ? data : {});
      } catch {
        // Best-effort: without a result the badges simply don't render.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verificationUidsKey]);

  // Event-card search: matches step titles against the summary tier's full
  // (unpaginated) history, then — since the visible timeline is paginated —
  // loads whatever older pages are needed to actually reveal a match before
  // scrolling to it.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightedEventKey, setHighlightedEventKey] = useState<string | null>(null);

  // Key search: type a transaction key to jump straight to it. Reuses the
  // kanban search endpoint (authkey/kanban) scoped to the current
  // transaction's team, plus whatever client/date/status filters are
  // currently active on the transactions list page (SelectionContext for
  // client+date, the dashboardFilters localStorage snapshot for the rest) so
  // results match what the user would see there.
  const [keySearchOpen, setKeySearchOpen] = useState(false);
  const [keySearchQuery, setKeySearchQuery] = useState('');
  const [keySearchLoading, setKeySearchLoading] = useState(false);
  const [keySearchResults, setKeySearchResults] = useState<any[]>([]);
  const keySearchBoxRef = useRef<HTMLDivElement>(null);

  // Flip the loader on synchronously, in the same click/Enter handler that
  // fires navigate() — the transUid-change effect further down also sets
  // loading, but it only runs on the render *after* the route param updates,
  // which left a one-frame gap (stale content, no loader) between clicking a
  // result and the spinner appearing.
  const goToKeySearchResult = (row: any) => {
    // Flip to the loading state synchronously, in the same click/Enter handler
    // that fires navigate(). The transUid-change effect does this too, but it
    // only runs on the render *after* the route param updates — so without
    // this the previous transaction's content stays on screen for a frame
    // instead of the loader.
    activeTxnUidRef.current = row.uid;
    setLoading(true);
    setSummaryLoading(true);
    setTimelineLoading(true);
    setTimelineEvents([]);
    setTransactionDetails([]);
    navigate(`/transactions/details/${row.team?.uid || routeTeamUid}/${row.uid}/${row.key}`);
    setKeySearchOpen(false);
    setKeySearchQuery('');
    setKeySearchResults([]);
  };

  useEffect(() => {
    if (!keySearchOpen || !keySearchQuery.trim()) {
      setKeySearchResults([]);
      setKeySearchLoading(false);
      return;
    }
    setKeySearchLoading(true);
    const handle = setTimeout(async () => {
      let savedFilters: any = {};
      try {
        savedFilters = JSON.parse(localStorage.getItem('dashboardFilters') || '{}') || {};
      } catch {
        savedFilters = {};
      }
      try {
        const res: any = await transactionKanban({
          search: keySearchQuery.trim(),
          teams: [routeTeamUid],
          clients: selectedClient ? [selectedClient] : [],
          from: selectionDateRange?.startDate,
          to: selectionDateRange?.endDate,
          reportingStatus: savedFilters.reportingStatus || '',
          dateType: savedFilters.dateType || '',
          status: savedFilters.status || '',
          users: [],
          eventTypes: [],
          eventStatus: [],
          columnSize: 20,
          priorityOnly: false
        });
        const rows = (res?.data?.columns || []).flatMap((col: any) => col?.rows || []);
        setKeySearchResults(rows.filter((row: any) => row?.uid !== transUid).slice(0, 8));
      } catch {
        setKeySearchResults([]);
      } finally {
        setKeySearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [keySearchOpen, keySearchQuery, routeTeamUid, transUid, selectedClient, selectionDateRange, transactionKanban]);

  // Click-outside to close — unlike the timeline search popup (fixed to the
  // viewport with an explicit close button), this one sits inline in the
  // header with a dropdown that can be dismissed by clicking elsewhere.
  useEffect(() => {
    if (!keySearchOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (keySearchBoxRef.current && !keySearchBoxRef.current.contains(e.target as Node)) {
        setKeySearchOpen(false);
        setKeySearchQuery('');
        setKeySearchResults([]);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [keySearchOpen]);

  const { openCallDialog } = useOutgoingCall();
  const { database, firebaseUid, subscribeToTransactionLocations } = useFirebase();
  const [hasLiveTrackingData, setHasLiveTrackingData] = useState(false);
  // UIDs that actually have live GPS data in Firebase — i.e. the real tracked
  // drivers. Desktop/admin users who merely interacted with the txn are NOT here,
  // so the Live Driver roster filters to this set (+ legacy LOCATION-event users).
  const [liveTrackedUids, setLiveTrackedUids] = useState<Set<string>>(new Set());

  // Whether Firebase actually holds live location data for this transaction.
  // Process-level tracking writes straight to locations/{txnUid} with no
  // LOCATION step event, so this is what tells the details page to show the
  // Live Driver Tracking panel for those transactions.
  useEffect(() => {
    const uid = transactionDetails?.uid;
    // locations/{txnId} is id-shape gated (not auth-gated) so old unauthenticated
    // clients can write it — so the dashboard's read here does NOT need to wait for
    // Firebase Auth. Keep it un-gated so the live map still works if sign-in hiccups.
    if (!uid || !subscribeToTransactionLocations) {
      setHasLiveTrackingData(false);
      return;
    }
    const unsub = subscribeToTransactionLocations(uid, (data: any) => {
      const keys = data && typeof data === 'object' ? Object.keys(data) : [];
      setHasLiveTrackingData(keys.length > 0);
      setLiveTrackedUids(new Set(keys));
    });
    return () => {
      try { unsub && unsub(); } catch { /* noop */ }
    };
  }, [transactionDetails?.uid, subscribeToTransactionLocations]);

  const realtimeInFlightRef = useRef(false);
  const realtimeQueuedRef = useRef(false);
  const listenerStartedAtRef = useRef<number>(0);

  // Attachments state
  const [attachments, setAttachments] = useState<any>(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [showMediaSection, setShowMediaSection] = useState(false);

  // Transaction embed state
  const [showEmbedSection, setShowEmbedSection] = useState(false);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  console.log("videoUrl",videoUrl);
  
  // memoizing user data to prevent unnecessary re-renders
   const stableUser = useMemo(()=> ({
    id: user?.id || user?.uid,
    roleId: user?.role?.id || user?.resourceTypeId?.id,
    firstName: user?.firstName,
    lastName: user?.lastName,
    name: user?.name
   }),[    user?.id, 
          user?.uid, 
          user?.role?.id, 
          user?.resourceTypeId?.id,
          user?.firstName,
          user?.lastName,
          user?.name
    ]);
  
  // Check if the current user has access to edit a specific step
  const canEditStep = useCallback((stepItem: any) => {
    if (!canEditTransaction) {
      return false;
    }

    // If no actors defined in step, allow edit for anyone with EDIT_TRANSACTION permission
    if (!stepItem?.actors || !Array.isArray(stepItem.actors) || stepItem.actors.length === 0) {
      return true;
    }

    // Get user's role ID from user session
    const userRoleId = user?.role?.id || user?.resourceTypeId?.id;
    
    if (!userRoleId) {
      return false;
    }

    // Check if user's role is in the step's actors array
    return stepItem.actors.some((actor: any) => {
      const actorId = actor?.id || actor;
      return actorId === userRoleId;
    });
  }, [canEditTransaction, stableUser.roleId]); //instead of user added the stableUser or memoize user if does not work will replace with user only ! 

  // Ref to track if the initial (all-tiers) load is in progress, to prevent
  // duplicate calls from the two mount effects below firing back to back.
  const isLoadingRef = useRef(false);

  // The transaction the page is currently showing. Set before each tier load
  // kicks off, so a response for a transaction the user has already navigated
  // away from can't write its data — or clear the new transaction's loading
  // flags — when it lands late.
  const activeTxnUidRef = useRef<string | undefined>(transUid);
  const isStaleTxn = (uid: string) => !!activeTxnUidRef.current && activeTxnUidRef.current !== uid;

  // The backend's toJSON transform deletes _id (keeps the `id` virtual) —
  // uid/id are what these responses actually carry.
  const eventKeyOf = (e: any) => e?.uid || e?.id || e?._id;
  const eventTimeOf = (e: any) => e?.updateTime || e?.createdAt || e?.updatedAt || null;

  // Core tier: header/progress tracker/instruction panel/FormData. Cheap, no
  // event scan — this is what the page's main spinner keys off.
  const loadCoreDetails = useCallback(
    async (uid: string, showLoader = true) => {
      try {
        if (showLoader) setLoading(true);
        const res = await getTransactionHistoryCore(uid);
        if (isStaleTxn(uid)) return;
        if (res?.success && res?.data) {
          setTransactionDetails((prev: any) => ({ ...(Array.isArray(prev) ? {} : prev || {}), ...res.data }));
        }
      } catch (error) {
        console.error('Error loading core details:', error);
      } finally {
        if (showLoader && !isStaleTxn(uid)) setLoading(false);
      }
    },
    [getTransactionHistoryCore]
  );

  // Summary tier: full history, lightweight — AI Verification Summary, Assigned
  // Driver Info, Driver Actions Panel. Global aggregates, so never paginated.
  const loadSummary = useCallback(
    async (uid: string) => {
      try {
        setSummaryLoading(true);
        const res = await getTransactionHistorySummary(uid);
        if (isStaleTxn(uid)) return;
        if (res?.success && res?.data) {
          setTransactionDetails((prev: any) => ({ ...(Array.isArray(prev) ? {} : prev || {}), ...res.data }));
        }
      } catch (error) {
        console.error('Error loading summary:', error);
      } finally {
        if (!isStaleTxn(uid)) setSummaryLoading(false);
      }
    },
    [getTransactionHistorySummary]
  );

  // Timeline tier, initial page (no cursor) — replaces whatever was loaded.
  const loadTimelineFirstPage = useCallback(
    async (uid: string) => {
      try {
        setTimelineLoading(true);
        const res = await getTransactionHistoryTimeline(uid, {});
        if (isStaleTxn(uid)) return;
        if (res?.success && res?.data) {
          const events = res.data.events || [];
          setTimelineEvents(events);
          setTimelineCursor(res.data.nextCursor || null);
          setHasMoreTimeline(!!res.data.hasMore);
          setNewestLoadedAt(events[0] ? eventTimeOf(events[0]) : null);
        }
      } catch (error) {
        console.error('Error loading timeline:', error);
      } finally {
        if (!isStaleTxn(uid)) setTimelineLoading(false);
      }
    },
    [getTransactionHistoryTimeline]
  );

  // Timeline tier, "load more" on scroll-down — appends an older page.
  const loadTimelineMore = useCallback(
    async (uid: string) => {
      if (!hasMoreTimeline || timelineMoreRef.current || !timelineCursor) return;
      timelineMoreRef.current = true;
      try {
        setTimelineLoadingMore(true);
        const res = await getTransactionHistoryTimeline(uid, { before: timelineCursor });
        if (res?.success && res?.data) {
          // Dedupe on append, same key as mergeNewTimelineEvents: a page can still
          // arrive twice if resetAndReloadTimeline lands while this is in flight.
          setTimelineEvents((prev) => {
            const existingKeys = new Set(prev.map(eventKeyOf));
            const toAppend = (res.data.events || []).filter((e: any) => !existingKeys.has(eventKeyOf(e)));
            return [...prev, ...toAppend];
          });
          setTimelineCursor(res.data.nextCursor || null);
          setHasMoreTimeline(!!res.data.hasMore);
        }
      } catch (error) {
        console.error('Error loading more timeline events:', error);
      } finally {
        timelineMoreRef.current = false;
        setTimelineLoadingMore(false);
      }
    },
    [getTransactionHistoryTimeline, hasMoreTimeline, timelineCursor]
  );

  // Step-title matches for the search bar, computed against the summary
  // tier's full (unpaginated) history — restricted to FORM/INSTRUCTION
  // events since those are the only ones that render as a card in the
  // timeline. Ordered newest-first to match the timeline's display order.
  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [] as Array<{ eventKey: string; stepTitle: string }>;
    const list = Array.isArray(transactionDetails?.history) ? transactionDetails.history : [];
    return list
      .filter((ev: any) => ev.eventType === 'FORM' || ev.eventType === 'INSTRUCTION' || ev.eventType === 'REDIRECT')
      .filter((ev: any) => (ev?.step?.title || '').toLowerCase().includes(q))
      .reverse() // lite history is oldest-first; timeline displays newest-first
      .map((ev: any) => ({ eventKey: eventKeyOf(ev) ? String(eventKeyOf(ev)) : '', stepTitle: ev?.step?.title || '' }))
      .filter((m: any) => m.eventKey);
  }, [searchQuery, transactionDetails?.history]);

  useEffect(() => {
    setSearchMatchIndex(0);
  }, [searchQuery]);

  // The people "on" this transaction, from its step history. Two consumers with
  // deliberately different scopes:
  //   - `drivers` feeds the Assigned Driver Info panel: users *assigned* via
  //     RESOURCE fields only, keyed on resourceDataId (the canonical ResourceData
  //     ref the last-login lookup matches on). That panel is about who the work is
  //     assigned to, so step submitters are NOT added to it.
  //   - `callableUids` feeds Quick Call and Quick Chat: the assigned users PLUS
  //     anyone who has actually submitted a step, since either is someone you may
  //     need to reach about this transaction. Collects every id variant an entry
  //     carries, so it matches the dialogs' candidates whichever shape the history
  //     value happens to hold.
  const assignedUsers = useMemo(() => {
    // Name from the resource's scalar field values, mirroring how RESOURCE
    // fields render elsewhere on this page (skip technical keys, emails,
    // id-shaped values). Backend name wins in the panel; this is a fallback
    // before/if that's missing.
    const scalarName = (r: any): string => {
      const fields = r?.fields;
      if (!fields || typeof fields !== 'object') return '';
      return Object.entries(fields)
        .filter(([k, v]) => {
          if (['countryCode', 'team', 'id', '_id', 'Notes'].includes(k)) return false;
          if (v === null || v === undefined || typeof v === 'object') return false;
          const s = String(v).trim();
          if (!s || s.includes('@')) return false;
          if (/^[0-9a-fA-F]{24}$/.test(s) || /^[0-9a-fA-F-]{36}$/.test(s)) return false;
          return true;
        })
        .slice(0, 2)
        .map(([, v]) => String(v))
        .join(' ');
    };

    const drivers = new Map<string, { uid: string; name: string }>();
    const callableUids = new Set<string>();

    // Every id shape the logged-in user might appear under, so step-actor
    // collection below can skip them. Deliberately NOT applied to the RESOURCE
    // branch: a user genuinely assigned on a step has always been part of this
    // set, and narrowing that is outside this change.
    const selfIds = new Set<string>([user?.uid, user?.id].filter(Boolean).map(String));

    (Array.isArray(transactionDetails?.history) ? transactionDetails.history : []).forEach((item: any) => {
      // Whoever submitted / last updated this step. Reachable about the
      // transaction even when they were never assigned on it via a RESOURCE
      // field, so they join the call/chat roster but not the assigned-driver
      // panel. `item.user` is the canonical one: AuthkeysEvents.user is a
      // ResourceData ref populated with `uid`, which is exactly the key the
      // call/chat candidate lists are returned under. `updatedBy` is a fallback
      // — it usually carries only names/_id, so its ids may simply not match,
      // which set membership handles harmlessly.
      //
      // Self is skipped: dashboard users submit and edit steps constantly, and
      // there is no point offering to call or chat yourself. The call dialog
      // never showed self anyway (getCallCandidates drops the caller server-side)
      // but getChatParticipants does not, so without this the Quick Chat list
      // would offer a thread with yourself on most transactions.
      [item?.user, item?.updatedBy].forEach((u: any) => {
        if (!u || typeof u !== 'object') return;
        [u.uid, u._id, u.id].forEach((v: any) => {
          if (!v) return;
          if (selfIds.has(String(v))) return;
          callableUids.add(String(v));
        });
      });

      const data = Array.isArray(item?.data) ? item.data : [];
      data.forEach((f: any) => {
        if (f?.inputComponent !== 'RESOURCE') return;
        const vals = f?.value?.value;
        const arr = Array.isArray(vals) ? vals : vals ? [vals] : [];
        arr.forEach((r: any) => {
          if (!r || typeof r !== 'object') return;
          // Only user-type resources (drivers / app users); skip vehicles and
          // other non-user resource types.
          if (r.category && r.category !== 'user') return;
          // Canonical ResourceData reference — matches getLastLoginBatch
          // (uid or _id). NOT r.id, which is not the login-side id.
          const ruid = r.resourceDataId || r.id || r._id || r.uid;
          if (!ruid) return;
          const key = String(ruid);
          // Dedupe by id; keep the first non-empty name we find.
          const name = scalarName(r);
          if (!drivers.has(key) || (name && !drivers.get(key)?.name)) {
            drivers.set(key, { uid: key, name });
          }
          // Call candidates are keyed on ResourceData.uid — which is what
          // `r.id` holds — but collect every variant so a differently-shaped
          // history value still matches.
          [r.id, r.uid, r.resourceDataId, r._id].forEach((v: any) => {
            if (v) callableUids.add(String(v));
          });
        });
      });
    });

    return { drivers: Array.from(drivers.values()), callableUids: Array.from(callableUids) };
    // Primitive user ids, not the `user` object: ApiContext rebuilds its value on
    // every render, so depending on the object would recompute this constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionDetails?.history, user?.uid, user?.id]);

  // UIDs that are actually being live-tracked for this transaction — live GPS in
  // Firebase (process-level) plus legacy LOCATION-event submitters. Shared with
  // the Assigned Driver Info panel so it can surface each assigned driver's live
  // trip status (On Route / Delayed / Route Deviation) inline, now that the
  // duplicate roster row that used to sit below the map has been removed.
  const trackedDriverUids = useMemo(() => {
    const set = new Set<string>(liveTrackedUids);
    (Array.isArray(transactionDetails?.history) ? transactionDetails.history : []).forEach((it: any) => {
      if (it?.eventType !== 'LOCATION') return;
      const u = it?.user?.uid;
      if (u) set.add(String(u));
    });
    return Array.from(set);
  }, [transactionDetails?.history, liveTrackedUids]);

  // Jump to a search match. If its card isn't loaded yet (the timeline is
  // paginated), fetch older pages directly here rather than via
  // loadTimelineMore — each page's own response is checked for the match
  // immediately, instead of round-tripping through this closure's
  // (possibly stale) timelineEvents state — until it turns up or the
  // history is exhausted. Then scroll to and briefly highlight the card.
  const goToMatch = useCallback(
    async (index: number) => {
      if (!transUid || searchMatches.length === 0) return;
      const clamped = ((index % searchMatches.length) + searchMatches.length) % searchMatches.length;
      setSearchMatchIndex(clamped);
      const target = searchMatches[clamped];

      const alreadyVisible = timelineEvents.some((e: any) => String(eventKeyOf(e)) === target.eventKey);
      if (!alreadyVisible) {
        setSearchLoading(true);
        try {
          let cursor = timelineCursor;
          let more = hasMoreTimeline;
          let found = false;
          let guard = 0;
          while (!found && more && guard < 200) {
            const res = await getTransactionHistoryTimeline(transUid, { before: cursor || undefined });
            if (!res?.success || !res?.data) break;
            const events = res.data.events || [];
            setTimelineEvents((prev) => [...prev, ...events]);
            cursor = res.data.nextCursor || null;
            more = !!res.data.hasMore;
            setTimelineCursor(cursor);
            setHasMoreTimeline(more);
            found = events.some((e: any) => String(eventKeyOf(e)) === target.eventKey);
            guard++;
          }
          if (!found) {
            showToast('Could not locate that step in the timeline', 'error');
            return;
          }
        } catch (error) {
          console.error('Error loading timeline pages for search:', error);
          showToast('Search failed to load that step', 'error');
          return;
        } finally {
          setSearchLoading(false);
        }
      }

      setHighlightedEventKey(target.eventKey);
      // Double rAF: setTimelineEvents above may still be mid-flight through
      // React's commit when this resumes — wait a full paint cycle so the
      // target card actually exists in the DOM before scrolling to it.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const selector = `[data-event-key="${(window as any).CSS?.escape ? (window as any).CSS.escape(target.eventKey) : target.eventKey}"]`;
          const el = document.querySelector(selector);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
      window.setTimeout(() => {
        setHighlightedEventKey((k) => (k === target.eventKey ? null : k));
      }, 2200);
    },
    [transUid, searchMatches, timelineEvents, timelineCursor, hasMoreTimeline, getTransactionHistoryTimeline, showToast]
  );

  // Eager pagination: as soon as one page lands, immediately fire the next
  // "before" request — chaining through every remaining page — instead of
  // waiting for the user to scroll near the bottom. This effect re-fires
  // after every page (timelineCursor/hasMoreTimeline change on each
  // response) and stops naturally once the backend reports hasMore: false.
  useEffect(() => {
    if (!transUid || !hasMoreTimeline || timelineLoadingMore || timelineLoading || !timelineCursor) return;
    loadTimelineMore(transUid);
  }, [transUid, hasMoreTimeline, timelineCursor, timelineLoadingMore, timelineLoading, loadTimelineMore]);

  // Realtime top-up: fetch events newer than the newest one already loaded and
  // prepend them, without disturbing already-loaded older pages or scroll
  // position. Used for Firebase pushes and "I just submitted a new step" — both
  // are "a new event appeared" cases. Does NOT catch edits to an
  // already-loaded older event (see refreshAfterEdit below for that case).
  const mergeNewTimelineEvents = useCallback(
    async (uid: string) => {
      try {
        const res = await getTransactionHistoryTimeline(uid, { after: newestLoadedAt || undefined });
        const fresh = res?.success ? res?.data?.events || [] : [];
        if (fresh.length > 0) {
          setTimelineEvents((prev) => {
            const existingKeys = new Set(prev.map(eventKeyOf));
            const toPrepend = fresh.filter((e: any) => !existingKeys.has(eventKeyOf(e)));
            return [...toPrepend, ...prev];
          });
          setNewestLoadedAt(eventTimeOf(fresh[0]));
        }
      } catch (error) {
        console.error('Error merging new timeline events:', error);
      }
    },
    [getTransactionHistoryTimeline, newestLoadedAt]
  );

  // Hard reset of the timeline to a fresh first page — for explicit manual
  // refresh, and for edits/redos where an already-loaded (possibly older)
  // event changed and a lightweight "after" merge wouldn't catch it.
  const resetAndReloadTimeline = useCallback(
    async (uid: string) => {
      setTimelineEvents([]);
      setTimelineCursor(null);
      setHasMoreTimeline(true);
      setNewestLoadedAt(null);
      await loadTimelineFirstPage(uid);
    },
    [loadTimelineFirstPage]
  );

  // Initial load / switching to a different transaction: all three tiers in
  // parallel, guarded against the duplicate mount effects below both firing.
  const loadAllTiers = useCallback(
    async (uid: string, forceRefresh = false, showLoader = true) => {
      if (isLoadingRef.current && !forceRefresh) {
        console.log('Already loading, skipping duplicate call');
        return;
      }
      try {
        isLoadingRef.current = true;
        await Promise.all([
          loadCoreDetails(uid, showLoader),
          loadSummary(uid),
          loadTimelineFirstPage(uid)
        ]);
      } finally {
        isLoadingRef.current = false;
        if (isInitialLoad) setIsInitialLoad(false);
      }
    },
    [loadCoreDetails, loadSummary, loadTimelineFirstPage, isInitialLoad]
  );

  // "A new event appeared" refresh (Firebase push, or this user just submitted
  // a step): core + summary fully replace (cheap, global), timeline merges the
  // new event(s) at the top without resetting scroll.
  const refreshAfterNewEvent = useCallback(
    async (uid: string) => {
      await Promise.all([
        loadCoreDetails(uid, false),
        loadSummary(uid),
        mergeNewTimelineEvents(uid)
      ]);
    },
    [loadCoreDetails, loadSummary, mergeNewTimelineEvents]
  );

  // "An existing event changed" refresh (edit, redo): core + summary replace,
  // timeline does a full reset since the changed event could be anywhere in
  // already-loaded pages, not just the newest one.
  const refreshAfterEdit = useCallback(
    async (uid: string) => {
      await Promise.all([
        loadCoreDetails(uid, false),
        loadSummary(uid),
        resetAndReloadTimeline(uid)
      ]);
    },
    [loadCoreDetails, loadSummary, resetAndReloadTimeline]
  );

  useEffect(() => {
    if (transUid && isInitialLoad) {
      activeTxnUidRef.current = transUid;
      loadAllTiers(transUid);
    } else if (!transUid) {
      // No transUid (e.g. the "Add Transaction" flow, which routes to
      // /transactions/details/:teamUid with no transaction id yet) — there's
      // nothing to fetch on any tier, so nothing should show as loading.
      // Only when transUid is genuinely absent: switching to a *different*
      // transaction also lands here, and clearing the flags then left a gap
      // where the timeline was empty but not marked loading, which rendered
      // the "No Transaction Details History" empty state mid-load. That case
      // is handled by the transaction-switch effect below.
      setLoading(false);
      setSummaryLoading(false);
      setTimelineLoading(false);
    }
  }, [transUid, isInitialLoad]);

  // Add this ref near your other refs
const prevTransUidRef = useRef<string | undefined>(undefined);

// Add this NEW useEffect — keep your existing ones untouched
useEffect(() => {
  // On initial mount, just record the uid and exit
  if (prevTransUidRef.current === undefined) {
    prevTransUidRef.current = transUid;
    return;
  }

  // Only fires when transUid actually changes to a different value
  if (prevTransUidRef.current === transUid) return;

  prevTransUidRef.current = transUid;

  if (!transUid) return;

  activeTxnUidRef.current = transUid;

  // Mark every tier as loading up front, in the same commit that clears the
  // old transaction's data. Otherwise there's a window where the events are
  // already empty but no tier has flipped its flag yet, and the timeline
  // renders its "no history" empty state instead of the skeletons.
  setLoading(true);
  setSummaryLoading(true);
  setTimelineLoading(true);

  // Reset state for the new transaction
  setTransactionDetails([]);
  setAttachments(null);
  setEditingStepId(null);
  setEditingStepItem(null);
  setImagesPreview([]);
  setShowMediaSection(false);
  setShowEmbedSection(false);
  setEmbedUrl(null);
  setTimelineEvents([]);
  setTimelineCursor(null);
  setHasMoreTimeline(true);
  setNewestLoadedAt(null);
  isLoadingRef.current = false;

  loadAllTiers(transUid, true);
  loadAttachments(transUid);
}, [transUid]);

  // Fetch a fresh signed embed URL each time the section is opened
  useEffect(() => {
    const key = transactionDetails?.key;
    if (!key || !canViewTransactionEmbed || !showEmbedSection) return;
    setEmbedUrl(null);
    setEmbedLoading(true);
    getTransactionEmbedUrl(key)
      .then((res: any) => {
        if (res?.data?.iframeUrl) setEmbedUrl(res.data.iframeUrl);
      })
      .catch(() => {/* silently ignore */})
      .finally(() => setEmbedLoading(false));
  }, [showEmbedSection, transactionDetails?.key, canViewTransactionEmbed]);

  useEffect(() => {
    if (!open || !transUid) return;
    getTransactionHistoryDetails(transUid)
      .then((res: any) => {
        if (res?.success && Array.isArray(res?.data?.history)) {
          setMissedCallHistory(res.data.history);
        }
      })
      .catch(() => {/* silently ignore */});
  }, [open, transUid, getTransactionHistoryDetails]);

  const handlePlayVideo = (url: string) => {
  setVideoUrl(url);
  setShowVideo(true);
}; 

  // Dedicated refresh function with toast notification
  const handleRefreshDetails = async () => {
    if (!transUid) {
      showToast('No transaction ID available to refresh', 'error');
      return;
    }
    
    try {
      await Promise.all([
        loadCoreDetails(transUid, true),
        loadSummary(transUid),
        resetAndReloadTimeline(transUid),
        loadAttachments(transUid)
      ]);
      showToast('Transaction details refreshed successfully', 'success');

    } catch (error) {
      showToast('Failed to refresh transaction details', 'error');
    }
  };

  function formatToLocalTime(value: string) {
    return formatDateWithTimezoneDisplay(value); // No time, but includes timezone if available
  }

    // Update the useEffect to only run on initial load or transUid change

  // Load attachments
  const loadAttachments = useCallback(async (uid: string, showLoader = true) => {
    try {
      if (showLoader) {
        setAttachmentsLoading(true);
      }
      const result = await getTransactionAttachments(uid);
      if (result?.success && result?.data) {
        setAttachments(result.data);
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
    } finally {
      if (showLoader) {
        setAttachmentsLoading(false);
      }
    }
  }, [getTransactionAttachments]);

  const refreshDetailsFromRealtime = useCallback(async () => {
    if (!transUid) return;

    if (saving) {
      realtimeQueuedRef.current = true;
      return;
    }

    if (realtimeInFlightRef.current) {
      realtimeQueuedRef.current = true;
      return;
    }

    realtimeInFlightRef.current = true;
    try {
      do {
        if (saving) {
          realtimeQueuedRef.current = true;
          break;
        }

        realtimeQueuedRef.current = false;
        // A realtime push means "something changed elsewhere" — treat it as a
        // new-event merge, same as this user submitting a step themselves.
        await Promise.all([
          refreshAfterNewEvent(transUid),
          loadAttachments(transUid, false)
        ]);
      } while (realtimeQueuedRef.current && !saving);
    } finally {
      realtimeInFlightRef.current = false;
    }
  }, [loadAttachments, refreshAfterNewEvent, saving, transUid]);

  useEffect(() => {
    if (!saving && realtimeQueuedRef.current) {
      void refreshDetailsFromRealtime();
    }
  }, [refreshDetailsFromRealtime, saving]);

  useEffect(() => {
    // transaction-details reads require auth != null — wait for Firebase sign-in.
    if (!database || !firebaseUid || !transUid || !transactionDetails) {
      return;
    }

    const teamIds = [
      transactionDetails?.team?.uid,
      transactionDetails?.team?.id,
      transactionDetails?.team?._id,
      transactionDetails?.teamUid,
      transactionDetails?.teamId
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    const clientIds = [
      transactionDetails?.client?.uid,
      transactionDetails?.client?.id,
      transactionDetails?.client?._id,
      transactionDetails?.clientUid,
      transactionDetails?.clientId
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (teamIds.length === 0 && clientIds.length === 0) {
      return;
    }

    const paths = new Set<string>();
    teamIds.forEach((id) => {
      paths.add(`transaction-details/team/${id}`);
      paths.add(`transaction-details/${id}`);
    });
    clientIds.forEach((id) => {
      paths.add(`transaction-details/client/${id}`);
    });

    const teamContextIds = new Set(teamIds);
    const clientContextIds = new Set(clientIds);
    const transactionIdCandidates = new Set(
      [transUid, transactionDetails?._id, transactionDetails?.id]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    );

    listenerStartedAtRef.current = Date.now();

    const unsubscribers: Array<() => void> = [];

    paths.forEach((path) => {
      // Bound the initial sync of the accumulating transaction-details node.
      const streamRef = query(ref(database, path), limitToLast(20));
      const unsubscribe = onChildAdded(streamRef, (snapshot) => {
        const payload = snapshot.val() as unknown;
        if (!isTransactionDetailsEvent(payload)) {
          return;
        }

        const occurredAtTs = Date.parse(payload.occurredAt);
        if (Number.isNaN(occurredAtTs) || occurredAtTs < listenerStartedAtRef.current) {
          return;
        }

        const payloadKeyCandidates = [payload.keyUid, payload.keyId]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
        const hasKeyInPayload = payloadKeyCandidates.length > 0;
        const keyMatches = payloadKeyCandidates.some((value) => transactionIdCandidates.has(value));

        if (hasKeyInPayload && !keyMatches) {
          return;
        }

        if (!hasKeyInPayload) {
          const payloadTeamCandidates = [payload.teamId, payload.teamUid]
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
          const payloadClientCandidates = [payload.clientId, payload.clientUid]
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

          const teamMatches = payloadTeamCandidates.some((value) => teamContextIds.has(value));
          const clientMatches = payloadClientCandidates.some((value) => clientContextIds.has(value));

          if (payloadTeamCandidates.length > 0 && !teamMatches) {
            return;
          }
          if (payloadClientCandidates.length > 0 && !clientMatches) {
            return;
          }
          if (payloadTeamCandidates.length === 0 && payloadClientCandidates.length === 0) {
            return;
          }
        }

        realtimeQueuedRef.current = true;
        void refreshDetailsFromRealtime();
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      realtimeQueuedRef.current = false;
    };
  }, [database, firebaseUid, refreshDetailsFromRealtime, transUid, transactionDetails]);

  useEffect(() => {
    if (transUid && isInitialLoad) {
      activeTxnUidRef.current = transUid;
      loadAllTiers(transUid);
      loadAttachments(transUid);
    } else if (!transUid) {
      // Same guard as the sibling effect above: only clear the flags when
      // there's genuinely nothing to fetch. This effect runs *after* the
      // transaction-switch effect in the same commit, so an unguarded else
      // here flipped the flags back to false immediately after the switch
      // marked them loading — the loader vanished before any request landed.
      setLoading(false);
      setSummaryLoading(false);
      setTimelineLoading(false);
    }
  }, [transUid, isInitialLoad]);  // Removed loadTransactionDetails dependency to prevent loops

  console.log("Component Type:", transactionDetails?.step?.component?.componentType);

  const getHistoryData = async (e: any) => {
    let allImages: any[] = [];

    // Get images from data fields
    if (e?.data?.length) {
      e.data.forEach((field: any) => {
        if (field?.value?.files?.length) {
          field.value.files.forEach((file: any) => {
            const ext = file.file_name?.split('.').pop()?.toLowerCase() || '';
            // Only add image files to viewer (not videos or other files)
            if (file.url && ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
              allImages.push({ src: file.url });
            }
          });
        }
      });
    }

    if (e?.files?.length) {
      e.files.forEach((file: any) => {
        const ext = file.file_name?.split('.').pop()?.toLowerCase() || '';
        if (file.url && ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
          allImages.push({ src: file.url });
        }
      });
    }

    setImagesPreview(allImages);
  };

  const handleImage=(fieldImage:any,index:number)=>{
    setImagesPreview(fieldImage);
    setCurrentImage(index);
    setViewerIsOpen(true);
  }

  const refreshAfterFormSubmit = useCallback(async () => {
    if (transUid) {
      setFormRefreshLoading(true);
      try {
        // Submitting the current step's form creates a new event — merge it
        // in at the top rather than resetting the whole timeline.
        await refreshAfterNewEvent(transUid);
      } finally {
        setFormRefreshLoading(false);
      }
    }
  }, [refreshAfterNewEvent, transUid]);

  async function onSuccess(): Promise<void> {
    if (transUid) {
      await refreshAfterFormSubmit();
      
      // Push a new state to history to prevent going back to form after successful submission
      const newState = { page: 'transaction-details', formSubmitted: true, timestamp: Date.now() };
      window.history.pushState(newState, '', window.location.href);
    }
  }

  const hasFormFields =
    (transactionDetails?.step?.component?.fields?.length ?? 0) > 0 || 
    (transactionDetails?.component?.fields?.length ?? 0) > 0 ||
    // Also consider instruction components as having content to display
    transactionDetails?.step?.stepType === 'INSTRUCTION' ||
    transactionDetails?.step?.component?.componentType === 'INSTRUCTION';

  console.log('Details - transactionDetails?.step?.stepType:', transactionDetails?.step?.stepType);
  console.log('Details - transactionDetails?.step?.component?.componentType:', transactionDetails?.step?.component?.componentType);
  console.log('Details - hasFormFields:', hasFormFields);

  // Handle form submission and history management
  useEffect(() => {
    // When transaction details are loaded and form fields become unavailable,
    // push a new state to prevent going back to the form
    if (transactionDetails && !hasFormFields && transUid) {
      const newState = { page: 'transaction-details', formSubmitted: true };
      window.history.pushState(newState, '', window.location.href);
    }

    // if(transactionDetails?.childTxn?.enabled)
    // { 
    //   handleOpenChildDialog();
    // }

  }, [transactionDetails, hasFormFields, transUid]);

  // Edit functionality
  const handleEditStep = (stepItem: any) => {
    console.log('handleEditStep called with:', stepItem);
    console.log('stepItem.data:', stepItem?.data);
    console.log('stepItem structure:', JSON.stringify(stepItem, null, 2));
    
    // Try to get the ID from either _id or id field
    const itemId = stepItem._id || stepItem.id;
    console.log('Extracted itemId:', itemId);
    
    if (!itemId) {
      console.error('No valid ID found in stepItem:', stepItem);
      return;
    }
    
    // Validate that stepItem has the expected structure
    if (!stepItem.data || !Array.isArray(stepItem.data)) {
      console.warn('stepItem.data is not an array or is missing:', stepItem.data);
      // You might want to show an error message to the user
      alert('This step cannot be edited because it has no editable fields.');
      return;
    }
    
    // Scroll to the top of the page
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setEditingStepId(itemId);
    setEditingStepItem(stepItem);
     
  };

  const handleCancelEdit = () => {
    setEditingStepId(null);
    setEditingStepItem(null);
  };

  const handleSaveStep = async (stepId: string, updatedData: any) => {
    console.log('handleSaveStep called with stepId:', stepId);
    
    setSaving(true);
    console.log('Setting saving to true');
    
    try {
      console.log('Saving payload:', JSON.stringify(updatedData, null, 2));
      
      const result = await updateTransactionHistoryStep(stepId, { data: updatedData });   
      // Show success toast
      showToast('Form Edited successfully', 'success');
      console.log('API response:', result);
      
      // Reload transaction details to get updated data. This edits an
      // *existing* event (possibly on an already-scrolled-past page), so a
      // top-up merge wouldn't catch it — do a full timeline reset.
      if (transUid) {
        console.log('Reloading transaction details for:', transUid);
        await refreshAfterEdit(transUid);
      }

      setEditingStepId(null);
      setEditingStepItem(null);
      
      console.log('Step updated successfully');
    } catch (error: any) {
      console.error('Error updating step:', error);
      showToast(error.message, 'error');
      // You might want to show an error toast here
    } finally {
      setSaving(false);
      console.log('Setting saving to false');
    }
  };

  // Redo functionality
  const handleRedoStep = async (stepItem: any) => {
    console.log('handleRedoStep called with:', stepItem);
    
    if (!transUid) {
      console.error('No transaction UID available');
      showToast('Error: No transaction ID available', 'error');
      return;
    }

    const stepUid = stepItem?.step?.uid || stepItem?.step?._id || stepItem?.step?.id;
    console.log('Extracted stepUid:', stepUid);
    
    if (!stepUid) {
      console.error('No step UID found in stepItem:', stepItem);
      showToast('Error: No step ID available for redo', 'error');
      return;
    }

    // Reset modal state and open
    setStepToRedo(stepItem);
    setRedoType('current');
    setSelectedRoleUid('');
    setRedoConfirmOpen(true);

    // Prefetch roles for role-based option — only 'user' category (roles),
    // scoped to this transaction's client. Excludes 'others' resource types.
    setRolesLoading(true);
    try {
      const clientUID =
        transactionDetails?.client?.uid ||
        transactionDetails?.clientUid ||
        transactionDetails?.clientId ||
        undefined;
      const rolesData = await getResourceTypes(clientUID, 'user');
      const roles = rolesData?.data || rolesData?.resourceTypes || rolesData || [];
      setAvailableRoles(Array.isArray(roles) ? roles : []);
    } catch {
      setAvailableRoles([]);
    } finally {
      setRolesLoading(false);
    }
  };

  function toSentenceCase(input: string): string {
      const quotedMap: Record<number, string> = {};
      let idx = 0;

      // Step 1: Replace "quoted" text with lowercase placeholders
      //         Store the UPPERCASED version in quotedMap
      const withPlaceholders = input.replace(
        /"([^"]*)"/g,
        (_: string, inner: string): string => {
          const key = `__q${idx}__`;
          quotedMap[idx] = inner.toUpperCase();
          idx++;
          return key;
        }
      );

      // Step 2: Lowercase everything, then capitalize only the first char
      const sentenceCased = withPlaceholders
        .toLowerCase()
        .replace(/^\w/, (c: string): string => c.toUpperCase());

      // Step 3: Restore placeholders with their UPPERCASE values
      return sentenceCased.replace(
        /__q(\d+)__/g,
        (_: string, i: string): string => quotedMap[Number(i)]
      );
    }

  const formatResourceValue = (value: any): string => {
    if (Array.isArray(value) && value.length > 0) {
      const firstResource = value[0];
      if (typeof firstResource === 'object' && firstResource !== null) {
        // Check if it's a resource object with fields
        if (firstResource.fields && Object.keys(firstResource.fields).length > 0) {
          // Display first 2 resources
          const resourcesToShow = value.slice(0, 2);
          const resourceStrings = resourcesToShow.map((resource: any) => {
            if (resource.fields && Object.keys(resource.fields).length > 0) {
              // Show first 2 fields from each resource
              return Object.values(resource.fields)
                .slice(0, 2)
                .map(v => String(v))
                .join(', ');
            }
            return 'Resource';
          });
          
          const result = resourceStrings.join(' | ');
          return value.length > 2 ? result + ' ...' : result;
        }
        // If no fields, try to display any meaningful property
        return 'Resource Data';
      }
    }
    return String(value);
  };

  // A dynamic field's inputComponent (SELECT/RADIO/CHECKBOX) does not reveal
  // that its chosen values were sourced from a DATE or RESOURCE field, so those
  // values arrive as objects. Format by SHAPE so an object never reaches JSX
  // directly (which throws "Objects are not valid as a React child") or renders
  // as "[object Object]".
  const formatDynamicScalar = (val: any): any => {
    if (isValidDateValue(val)) return formatToLocalTime(val);
    if (val && typeof val === 'object') {
      if ('fields' in val || 'id' in val || '_id' in val) return formatResourceValue([val]);
      return JSON.stringify(val);
    }
    return val;
  };

  const getFieldSchema = (historyItem: any, fieldItem: any) => {
    const schemaFields = historyItem?.step?.component?.fields || historyItem?.step?.fields || [];

    return schemaFields.find((schemaField: any) => {
      const schemaUid = schemaField?.uid || schemaField?.field;
      return schemaUid === fieldItem?.field || schemaUid === fieldItem?.uid || schemaField?.question === fieldItem?.question;
    });
  };

  const getCommaSeparatedCount = (rawValue: any) => {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];

    return values
      .flatMap((value) => String(value ?? '').split(/[\s,]+/))
      .map((value) => value.trim())
      .filter(Boolean).length;
  };

  // Function to handle confirmed redo action
  const handleConfirmedRedo = async () => {
    if (!stepToRedo || !transUid) return;

    const stepUid = stepToRedo?.step?.uid || stepToRedo?.step?._id || stepToRedo?.step?.id;

    if (redoType === 'role' && !selectedRoleUid) {
      showToast('Please select a role', 'error');
      return;
    }

    try {
      setSaving(true);
      let result: any;

      if (redoType === 'current') {
        result = await redoStep(transUid, stepUid);
      } else if (redoType === 'full') {
        result = await resetToStep(transUid, stepUid);
      } else {
        result = await resetToStepByRole(transUid, stepUid, selectedRoleUid);
      }

      if (result.success) {
        showToast('Step has been reset successfully', 'success');

        // Redo flips isRedo on an existing (possibly already-loaded) event and
        // changes the current step — full reset, not a top-up merge.
        await refreshAfterEdit(transUid);

        // Remove the resetSteps logic - we'll use the API's isRedo flag instead
      } else {
        showToast(result.message || 'Failed to reset step', 'error');
      }
    } catch (error) {
      console.error('Error resetting step:', error);
      showToast('Failed to reset step', 'error');
    } finally {
      setSaving(false);
      setRedoConfirmOpen(false);
      setStepToRedo(null);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '98vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        p:0
      }}
    >
      <MainCard
        content={false}
        sx={{
          flex: 1,
          borderRadius: 2,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 0,
          pt:0
          // No cursor pointer, no hover effect
        }}
      >
        
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8'
            }
          }}
        >
          {loading && (
            // Thin top-of-page indicator instead of blocking the whole page —
            // the header/progress tracker below render immediately with
            // whatever core data has arrived so far (blank fields briefly,
            // not a spinner covering everything). Matches the same
            // "don't block on data you don't have yet" approach used for the
            // timeline/summary panel skeletons.
            <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 100 }} />
          )}
          {(
            <Grid container spacing={1}>
              <Grid item xs={12} md={12} sx={{ position: "sticky",top: 0, zIndex: 99,background:"#fff",pt:0 }}>
                <Box
                          sx={{
                            border: 'none',
                            outline: 'none',
                            color: '#fff',
                            // position: 'sticky',
                            px: 0.5,
                            py: 0.7,
                            // minHeight: 80,
                            top: 0,
                            zIndex: 2,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            backgroundColor: 'primary.dark',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                            // boxShadow: '0 2px 8px rgba(19, 45, 118, 0.2)'
                          }}
                        >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>    
                      <IconButton
                        onClick={() => navigate('/transactions')}
                        sx={{
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        <ArrowBack />
                      </IconButton>
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          textAlign: 'center',
                          fontSize:"14px"
                        }}
                      >
                        TRANSACTION DETAILS
                      </Typography>
                      </Box>
                          <Box ref={keySearchBoxRef} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              fontWeight={600}
                              variant="h6"
                              sx={{
                                color: '#fff',
                                fontSize: '12px',
                                letterSpacing: '0.025em',
                                textTransform: 'uppercase'
                              }}
                            >
                              {transactionDetails?.team?.settings?.transactionLabel || 'Transaction No'} :
                            </Typography>
                            {/* Key value and its search toggle read as one control:
                                a single shared border around both, split by a
                                1px partition, rather than two detached elements. */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: 1,
                                overflow: 'hidden'
                              }}
                            >
                              {keySearchOpen ? (
                                <InputBase
                                  autoFocus
                                  placeholder="Search key…"
                                  value={keySearchQuery}
                                  onChange={(e) => setKeySearchQuery(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (keySearchResults.length > 0) {
                                        goToKeySearchResult(keySearchResults[0]);
                                      }
                                    } else if (e.key === 'Escape') {
                                      setKeySearchOpen(false);
                                      setKeySearchQuery('');
                                      setKeySearchResults([]);
                                    }
                                  }}
                                  sx={{
                                    width: 180,
                                    px: '10px',
                                    color: '#fff',
                                    fontSize: '12px',
                                    '& input': { p: 0, py: '5px' },
                                    '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 }
                                  }}
                                />
                              ) : (
                                <Box
                                  component="span"
                                  onClick={() => setKeySearchOpen(true)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    px: '10px',
                                    py: '5px',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' }
                                  }}
                                >
                                  {transactionDetails?.key}
                                </Box>
                              )}
                              <Box sx={{ width: '1px', bgcolor: 'rgba(255, 255, 255, 0.3)', flexShrink: 0 }} />
                              <Tooltip title={keySearchOpen ? 'Close key search' : 'Search by key'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setKeySearchOpen((v) => {
                                      const next = !v;
                                      if (!next) {
                                        setKeySearchQuery('');
                                        setKeySearchResults([]);
                                      }
                                      return next;
                                    });
                                  }}
                                  sx={{
                                    color: '#fff',
                                    borderRadius: 0,
                                    px: '6px',
                                    bgcolor: keySearchOpen ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' }
                                  }}
                                >
                                  {keySearchOpen ? <Close sx={{ fontSize: 16 }} /> : <Search sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {keySearchOpen && keySearchQuery.trim() !== '' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  mt: 0.5,
                                  width: 260,
                                  maxHeight: 280,
                                  overflowY: 'auto',
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  zIndex: 1301
                                }}
                              >
                                {keySearchLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                                    <CircularProgress size={18} />
                                  </Box>
                                ) : keySearchResults.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
                                    No matching keys
                                  </Typography>
                                ) : (
                                  keySearchResults.map((row: any) => (
                                    <Box
                                      key={row.uid}
                                      onClick={() => goToKeySearchResult(row)}
                                      sx={{
                                        px: 1.5,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        color: 'text.primary',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-of-type': { borderBottom: 'none' },
                                        '&:hover': { bgcolor: 'action.hover' }
                                      }}
                                    >
                                      {row.key}
                                    </Box>
                                  ))
                                )}
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {transUid && (
                              <Tooltip title="Call user">
                                <span>
                                  <IconButton
                                    // Quick Call: same dialog as everywhere else, but
                                    // pre-filtered to the users on this transaction —
                                    // assigned via a step's RESOURCE field, or having
                                    // submitted a step. With neither present the
                                    // restriction is dropped and the full team shows.
                                    onClick={() => openCallDialog(transUid, { restrictToUids: assignedUsers.callableUids })}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                                      border: '1px solid rgba(255, 255, 255, 0.2)',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        transform: 'scale(1.05)'
                                      },
                                      transition: 'all 0.2s ease-in-out'
                                    }}
                                  >
                                    <Videocam sx={{ color: '#fff', fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={showMediaSection ? 'Hide media' : 'Show media'}>
                              <IconButton
                                onClick={() => setShowMediaSection((prev) => !prev)}
                                color="primary"
                                size="small"
                                sx={{
                                  bgcolor: showMediaSection ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                              >
                                <PhotoLibrary sx={{ color: '#fff', fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {canViewTransactionEmbed && transactionDetails?.key && (
                              <Tooltip title={showEmbedSection ? 'Hide report' : 'View report'}>
                                <IconButton
                                  onClick={() => setShowEmbedSection((prev) => !prev)}
                                  size="small"
                                  sx={{
                                    bgcolor: showEmbedSection ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': {
                                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <WebAsset sx={{ color: '#fff', fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* AI Detection indicator */}
                            {transactionDetails?.history?.some((h: any) => h.detectionResults?.length > 0) && (
                              <Tooltip title="AI Detection results available" placement="bottom">
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  bgcolor: 'rgba(76, 175, 80, 0.2)', border: '1px solid rgba(76, 175, 80, 0.4)',
                                  borderRadius: 1, px: 1, py: 0.25,
                                }}>
                                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4caf50', animation: 'pulse 2s infinite',
                                    '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
                                  }} />
                                  <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 600, letterSpacing: 0.5 }}>
                                    AI
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            {transUid && (
                              <Tooltip title={searchOpen ? 'Close search' : 'Search timeline events'}>
                                <IconButton
                                  onClick={() => {
                                    setSearchOpen((v) => {
                                      const next = !v;
                                      if (!next) {
                                        setSearchQuery('');
                                        setHighlightedEventKey(null);
                                      }
                                      return next;
                                    });
                                  }}
                                  color="primary"
                                  size="small"
                                  sx={{
                                    bgcolor: searchOpen ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': {
                                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Search sx={{ color: '#fff', fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Refresh transaction details">
                            <IconButton
                              onClick={handleRefreshDetails}
                              disabled={loading}
                              color="primary"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              <Refresh sx={{ color: '#fff', fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Communication">
                            <IconButton
                              color="primary"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                              onClick={toggleDrawer(true)}
                            >
                              <ChatBubbleOutlineIcon sx={{ color: '#fff', fontSize: 18 }}  />
                            </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        {searchOpen && (
                          // Fixed to the viewport, not the header — stays
                          // pinned top-right like a browser find bar as the
                          // timeline scrolls underneath it, instead of
                          // scrolling away with the page.
                          <Box
                            sx={{
                              position: 'fixed',
                              top: 76,
                              right: 24,
                              zIndex: 1300,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.75,
                              bgcolor: 'background.paper',
                              borderRadius: 1.5,
                              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <TextField
                              autoFocus
                              size="small"
                              placeholder="Search step name…"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (e.shiftKey) goToMatch(searchMatchIndex - 1);
                                  else goToMatch(searchMatchIndex);
                                } else if (e.key === 'Escape') {
                                  setSearchOpen(false);
                                  setSearchQuery('');
                                  setHighlightedEventKey(null);
                                }
                              }}
                              sx={{
                                width: 220,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 68, textAlign: 'center' }}>
                              {searchLoading
                                ? 'Loading…'
                                : searchQuery.trim() === ''
                                ? ''
                                : searchMatches.length === 0
                                ? 'No matches'
                                : `${searchMatchIndex + 1} of ${searchMatches.length}`}
                            </Typography>
                            <Tooltip title="Previous match">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={searchMatches.length === 0 || searchLoading}
                                  onClick={() => goToMatch(searchMatchIndex - 1)}
                                >
                                  <KeyboardArrowUp fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Next match">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={searchMatches.length === 0 || searchLoading}
                                  onClick={() => goToMatch(searchMatchIndex + 1)}
                                >
                                  <KeyboardArrowDown fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Close search">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSearchOpen(false);
                                  setSearchQuery('');
                                  setHighlightedEventKey(null);
                                }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                            {transactionDetails && (
                              <Box sx={{   borderBottom: '1px solid', borderColor: 'rgba(19, 45, 118, 0.1)' }}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: "4px", mb:0.5  }} >
                                  {transactionDetails?.additionalFields && (
                                    <Box
                                      sx={{
                                        // border: "1px solid",
                                        // borderColor: "divider",
                                        // borderRadius: 1,
                                        // p: "10px",
                                        // mb: 2,
                                      }}
                                    >

                                      <Grid container  sx={{display:"flex",justifyContent:"space-between", alignItems:"center"}} spacing={2}>
                                        {Object.entries(transactionDetails?.additionalFields) 
                                          .map(([key, value]: [string, any], idx: number) => (
                                           <Grid
                                                item
                                                key={idx}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: 600,
                                                    color: "primary.main",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    fontSize: "0.7rem",
                                                    lineHeight: 1,        
                                                  }}
                                                >
                                                  {toSentenceCase(key)}
                                                </Typography>

                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: "text.secondary",
                                                    fontSize: "0.7rem",
                                                    lineHeight: 1.2,        
                                                    display: "flex",
                                                    alignItems: "center",
                                                  }}
                                                >
                                                  {value !== undefined && value !== null && value !== ""
                                                    ? Array.isArray(value)
                                                      ? (value.length > 0 && typeof value[0] === "object" && value[0] !== null && "fields" in value[0])
                                                        ? formatResourceValue(value)
                                                        : value.map(v => formatDynamicScalar(v)).join(", ")
                                                      : isValidDateValue(value)
                                                        ? formatToLocalTime(value)
                                                        : String(value)
                                                    : "-"}
                                                </Typography>
                                              </Grid>
                                          ))}

                                        {transactionDetails?.team?.displayStepStatus && <Grid item xs={2} ><Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: "primary.main",
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.05em",
                                                  fontSize: "0.7rem",
                                                }}
                                              >
                                                Activity Status
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary", fontSize: "0.7rem", mt: 0.25 }}
                                              >
                                                {transactionDetails?.displayStatus || "--"}

                                              </Typography> 
                                              </Grid>}
                                        {transactionDetails?.team?.reportingStatus && 
                                        <Grid item alignItems='center' sx={{display:"flex", alignItems:"center" ,gap: 1}} >
                                          <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: "primary.main",
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.05em",
                                                  fontSize: "0.7rem",
                                                }}
                                              >
                                               Status
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary", fontSize: "0.7rem", }}
                                              >
                                                {transactionDetails?.displayKeyStatus || "--"}

                                              </Typography>
                                            </Grid>}
                                              {transactionDetails?.team?.process && 
                                            <Grid item xs={2} sx={{display:"flex", alignItems:"center" ,gap: 1}} >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: "primary.main",
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.05em",
                                                  fontSize: "0.7rem",
                                                }}
                                              >
                                                {"Process"}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary", fontSize: "0.7rem",  }}
                                              >
                                                {transactionDetails?.team?.name || "--"}

                                              </Typography>
                                            </Grid>}
                                      </Grid>
                                    </Box>
                                  )}
                                  {(!transactionDetails?.additionalFields ||(transactionDetails?.key && (transactionDetails?.team?.transactionFieldSelect?.length === 0) || !transactionDetails?.team?.transactionFieldSelect)) && (
                                    <Grid container sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",pr:1,   }} spacing={2}>
                                      <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 600, color: "primary.main", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem",   }}
                                        >
                                          {transactionDetails?.team?.settings?.transactionLabel || "Auth Key"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.7rem", textTransform:"capitalize"   }}>
                                          {transactionDetails?.key || "-"}
                                        </Typography>
                                      </Grid>

                                      <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 600, color: "primary.main", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem", lineHeight: 1 }}
                                        >
                                          Status
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                                          {transactionDetails?.displayStatus || "--"}
                                        </Typography>
                                      </Grid>

                                      <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 600, color: "primary.main", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem", lineHeight: 1 }}
                                        >
                                          Reporting Status
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                                          {transactionDetails?.displayKeyStatus || "--"}
                                        </Typography>
                                      </Grid>

                                      <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 600, color: "primary.main", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem", lineHeight: 1 }}
                                        >
                                          Process
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                                          {transactionDetails?.team?.name || "--"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  )}
                                </Box>
                              
                            </Box>
                          )}
                    </Grid>

                    {/* === Redesign: Progress Tracker (full-width). Render
                        for existing transactions only; on the add-new-transaction
                        flow (!transUid) there's no transaction yet to track, so
                        the card is omitted rather than showing a permanent
                        loading spinner. */}
                    {transUid && (
                      <Grid item xs={12}>
                        <ProgressTrackerStepper
                          teamUid={
                            routeTeamUid ||
                            transactionDetails?.teamUid ||
                            transactionDetails?.team?.uid ||
                            transactionDetails?.team?.id ||
                            transactionDetails?.team?._id ||
                            transactionDetails?.teamId
                          }
                          currentKeyStatus={transactionDetails?.keyStatus}
                          currentKeyStatusName={
                            transactionDetails?.displayKeyStatus ||
                            (typeof transactionDetails?.keyStatus === 'object'
                              ? transactionDetails?.keyStatus?.name
                              : undefined)
                          }
                          fallbackLabel={
                            transactionDetails?.displayStatus ||
                            transactionDetails?.displayKeyStatus ||
                            transactionDetails?.keyStatus?.name ||
                            transactionDetails?.status
                          }
                        />
                      </Grid>
                    )}

                    {/* Assigned Driver Info — user-type resources assigned via a
                        step-history RESOURCE field, each with their last app-login.
                        Separate from live tracking: an assigned driver shows here
                        even if they never tracked or opened the app.
                        Roster comes from the shared `assignedUsers` memo — the same
                        set Quick Call filters the call dialog down to. Always
                        mounted: AssignedDriverPanel owns the decision of what to
                        show (loading skeleton, an explicit "no drivers assigned"
                        message, or the real roster). */}
                    <Grid item xs={12}>
                      <AssignedDriverPanel
                        participants={assignedUsers.drivers}
                        loading={summaryLoading}
                        verificationMap={verificationMap}
                        transactionUid={transactionDetails?.uid}
                        teamId={transactionDetails?.teamId}
                        teamUid={transactionDetails?.teamUid || transactionDetails?.team?.uid}
                        trackedUids={trackedDriverUids}
                      />
                    </Grid>

                    {/* === Redesign: Combined Live Driver Tracking + Alerts (full-width single card) ===
                        Only render when this transaction actually has LOCATION
                        events — there's no value in showing an empty tracking
                        card for shipments that never opted into GPS tracking. */}
                    {transactionDetails?.uid && (() => {
                      const locationEvents = Array.isArray(transactionDetails?.history)
                        ? transactionDetails.history.filter((it: any) => it?.eventType === 'LOCATION')
                        : [];
                      // Show the panel when this transaction has LOCATION events
                      // (legacy Location-step model) OR live data in Firebase
                      // (process-level tracking, which has no LOCATION events).
                      // Form-assigned drivers live in their own "Assigned Driver Info"
                      // panel (above) — this panel is live tracking only.
                      if (locationEvents.length === 0 && !hasLiveTrackingData) return null;

                      // Participants (name resolution for the live map) come from
                      // ALL event users — process-level tracking has no LOCATION
                      // events, but the tracked driver appears in the steps they
                      // submitted, so the map can still show a name, not a uid.
                      const participantMap = new Map<string, { uid: string; name: string }>();
                      (Array.isArray(transactionDetails?.history) ? transactionDetails.history : []).forEach((item: any) => {
                        const uid = item?.user?.uid;
                        if (!uid) return;
                        const name =
                          [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(' ').trim() ||
                          item?.user?.name ||
                          item?.userName ||
                          '';
                        if (!participantMap.has(uid) || (name && !participantMap.get(uid)?.name)) {
                          participantMap.set(uid, { uid, name });
                        }
                      });
                      // Tracked = live GPS in Firebase (process-level) or LOCATION
                      // events (legacy).
                      const trackedUids = new Set<string>(liveTrackedUids);
                      locationEvents.forEach((it: any) => {
                        const u = it?.user?.uid;
                        if (u) trackedUids.add(u);
                      });

                      // Roster = tracked drivers only (live GPS / LOCATION events).
                      // Tracked uids with no events are added name-less (LiveDriverPanel
                      // fills the name from the last-login lookup). Desktop/admin users
                      // (event-only, not tracked) stay excluded. Form-assigned drivers
                      // are listed separately in the Assigned Driver Info panel.
                      const rosterMap = new Map<string, { uid: string; name: string }>();
                      for (const uid of trackedUids) {
                        rosterMap.set(uid, participantMap.get(uid) || { uid, name: '' });
                      }
                      const participants = Array.from(rosterMap.values());

                      // Start / End driver cards are derived from LOCATION events
                      // only. Process-level tracking shows the live path from
                      // Firebase without these per-event start/end markers.
                      const startEventByUser = new Map<string, any>();
                      const endEventByUser = new Map<string, any>();
                      locationEvents.forEach((item: any) => {
                        const uid = item?.user?.uid;
                        if (!uid) return;
                        const ts = item?.createdAt ? new Date(item.createdAt).getTime() : NaN;

                        const existingStart = startEventByUser.get(uid);
                        const existingStartTs = existingStart?.createdAt
                          ? new Date(existingStart.createdAt).getTime()
                          : NaN;
                        if (!existingStart || (isFinite(ts) && (!isFinite(existingStartTs) || ts < existingStartTs))) {
                          startEventByUser.set(uid, item);
                        }

                        const existingEnd = endEventByUser.get(uid);
                        const existingEndTs = existingEnd?.createdAt
                          ? new Date(existingEnd.createdAt).getTime()
                          : NaN;
                        if (!existingEnd || (isFinite(ts) && (!isFinite(existingEndTs) || ts > existingEndTs))) {
                          endEventByUser.set(uid, item);
                        }
                      });

                      const formatTs = (iso: any): string | undefined => {
                        if (!iso) return undefined;
                        try {
                          return new Date(iso).toLocaleString();
                        } catch {
                          return undefined;
                        }
                      };
                      const pointFromEvent = (
                        ev: any,
                        fallbackName: string
                      ): {
                        name: string;
                        role?: string;
                        locationText?: string;
                        coordinates?: [number, number];
                        createdAt?: string;
                      } | undefined => {
                        if (!ev) return undefined;
                        const name =
                          [ev?.user?.firstName, ev?.user?.lastName].filter(Boolean).join(' ').trim() ||
                          ev?.user?.name ||
                          ev?.userName ||
                          fallbackName;
                        const roleName =
                          ev?.user?.role?.name ||
                          ev?.user?.role?.roleName ||
                          ev?.role?.name ||
                          '';
                        const locationText =
                          ev?.location?.location ||
                          ev?.location?.address ||
                          '';
                        const coords = Array.isArray(ev?.location?.coordinates)
                          ? (ev.location.coordinates as [number, number])
                          : undefined;
                        return {
                          name,
                          role: roleName || undefined,
                          locationText: locationText || undefined,
                          coordinates: coords,
                          createdAt: formatTs(ev?.createdAt)
                        };
                      };

                      const drivers = participants
                        .map((p) => {
                          const startEv = startEventByUser.get(p.uid);
                          const endEv = endEventByUser.get(p.uid);
                          const start = pointFromEvent(startEv, p.name || p.uid.slice(0, 6));
                          // Only surface "end" when it's a distinct event from
                          // start. Compare by object identity — the events are
                          // stored as direct references, so the same single
                          // event landing in both maps means we only have one
                          // LOCATION submission and there's no real end yet.
                          const end =
                            startEv && endEv && startEv !== endEv
                              ? pointFromEvent(endEv, p.name || p.uid.slice(0, 6))
                              : undefined;
                          if (!start && !end) return null;
                          const role = start?.role || end?.role;
                          return {
                            name: p.name || p.uid.slice(0, 6),
                            role,
                            start,
                            end
                          };
                        })
                        .filter(Boolean) as Array<{
                          name: string;
                          role?: string;
                          start?: ReturnType<typeof pointFromEvent>;
                          end?: ReturnType<typeof pointFromEvent>;
                        }>;
                      // Render the combined panel only when LOCATION events
                      // exist for this transaction (handled by the early
                      // return at the top of this IIFE). Transactions that
                      // never used live tracking get no card here at all.
                      return (
                        <Grid item xs={12}>
                          <LiveDriverPanel
                            transactionUid={transactionDetails.uid}
                            teamId={transactionDetails?.teamId}
                            teamUid={transactionDetails?.teamUid || transactionDetails?.team?.uid}
                            participants={participants}
                            trackedUids={Array.from(trackedUids)}
                            drivers={drivers}
                            pickup={transactionDetails.pickupAddress}
                            destination={transactionDetails.destinationAddress}
                          />
                        </Grid>
                      );
                    })()}

                    {/* === Driver Rest / Stop / Swap activity timeline ===
                        No loading placeholder here on purpose — this panel's
                        outlined style doesn't match the dark-header cards
                        around it, so a skeleton for it just reads as a
                        stray, unlabeled loading block. It simply appears
                        once there's data. */}
                    {Array.isArray(transactionDetails?.actionHistory) &&
                    transactionDetails.actionHistory.length > 0 && (
                      <Grid item xs={12}>
                        <DriverActionsPanel actionHistory={transactionDetails.actionHistory} />
                      </Grid>
                    )}

                    {/* === Redesign: AI Issues Summary (full-width) ===
                        Always mounted — AiIssuesSummary owns the decision of
                        whether to render anything: stays invisible for teams
                        with no AI detection configured at all, otherwise
                        always shows (loading skeleton, an explicit "no
                        checks yet" message, or real results). */}
                    <Grid item xs={12}>
                      <AiIssuesSummary
                        history={Array.isArray(transactionDetails?.history) ? transactionDetails.history : []}
                        loading={summaryLoading}
                        teamUid={
                          routeTeamUid ||
                          transactionDetails?.teamUid ||
                          transactionDetails?.team?.uid ||
                          transactionDetails?.team?.id ||
                          transactionDetails?.team?._id ||
                          transactionDetails?.teamId
                        }
                        onIssueClick={(stepUid) => {
                          if (!stepUid) return;
                          const el = document.getElementById(`step-${stepUid}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      />
                    </Grid>

                    {/* === Redesign: Next Steps & Forms (full-width v1; sticky side-by-side rail in a follow-up) === */}
                    {Array.isArray(transactionDetails?.currentSteps) && transactionDetails.currentSteps.length > 0 && (
                      <Grid item xs={12}>
                        <NextStepsPanel
                          teamUid={transactionDetails?.teamUid || transactionDetails?.team?.uid}
                          transactionUid={transactionDetails?.uid}
                          steps={(transactionDetails.currentSteps || [])
                            .filter((cs: any) => cs && typeof cs === 'object')
                            .map((cs: any): NextStep => ({
                              uid: cs?.uid,
                              title: cs?.title || 'Pending step',
                              componentType: cs?.component?.componentType || cs?.stepType,
                              instructionText: cs?.component?.settings?.instructions,
                              raw: cs
                            }))}
                          onSubmitted={() => {
                            if (transUid) refreshAfterNewEvent(transUid);
                          }}
                        />
                      </Grid>
                    )}

                     {/* displaying the media photos on the left and attachment on the right coming from the file  */}
                     {showMediaSection && attachments?.media?.length > 0 && (
                      <Grid item xs={12}>
                        <Box sx={{ px: 0.5 }}>
                          {(() => {
                            const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                            const images = attachments.media.filter((m: any) => {
                              const ext = (m._doc?.file_name || m.file_name || '').split('.').pop()?.toLowerCase() || '';
                              return imageExts.includes(ext);
                            }).slice().reverse();
                            const files = attachments.media.filter((m: any) => {
                              const ext = (m._doc?.file_name || m.file_name || '').split('.').pop()?.toLowerCase() || '';
                              return !imageExts.includes(ext);
                            }).slice().reverse();
                            return (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  p: 1,
                                  backgroundColor: '#fafbfc',
                                  alignItems:'stretch'
                                }}
                              >
                                {/* Images - Left Side */}
                                {images.length > 0 && (
                                  <Box
                                    sx={{
                                      width: files.length > 0 ? 'calc(50% - 0.5px)' : '100%',
                                      flex: files.length > 0 ? '0 0 calc(50% - 0.5px)' : '1 1 100%',
                                      minWidth: 0,
                                       display: 'flex',
                                      flexDirection: 'column',
                                      minHeight: 0,   
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                      <PhotoLibrary sx={{ fontSize: 14, color: 'primary.main' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Photos ({images.length})
                                      </Typography>
                                    </Box>
                                    <Box  sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        flexWrap: 'wrap',
                                        alignItems: 'flex-start',
                                        alignContent: 'flex-start',
                                        maxHeight: 100,         // set your desired height
                                        overflowY: 'auto',      // vertical scrollbar
                                        pr: 0.5,
                                        '&::-webkit-scrollbar': { width: '3px' },
                                        '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' },
                                      }}>
                                      {images.map((img: any, imgIdx: number) => (
                                        <Box
                                          key={imgIdx}
                                          sx={{
                                            width: 100,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.35,
                                          }}
                                        >
                                          <Box
                                            onClick={() => {
                                              const allImgPreviews = images.map((i: any) => ({ src: i.url,question: i.question || i._doc?.file_name || i.file_name, }));
                                              setImagesPreview(allImgPreviews);
                                              setCurrentImage(imgIdx);
                                              setViewerIsOpen(true);
                                            }}
                                            sx={{
                                              width: 65,
                                              height: 65,
                                              borderRadius: 1,
                                              overflow: 'hidden',
                                              cursor: 'pointer',
                                              border: '1px solid',
                                              borderColor: 'grey.300',
                                              transition: 'all 0.15s ease',
                                              '&:hover': {
                                                borderColor: 'primary.main',
                                                boxShadow: '0 2px 8px rgba(19,45,118,0.15)',
                                                transform: 'scale(1.05)',
                                              },
                                            }}
                                          >
                                            <img
                                              src={img.url}
                                              alt={img.question || img._doc?.file_name || `Photo ${imgIdx + 1}`}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              loading="lazy"
                                            />
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              width: '100%',
                                              fontSize: '0.6rem',
                                              lineHeight: 1.2,
                                              color: 'text.secondary',
                                              textAlign: 'center',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word',
                                            }}
                                            title={img.question || img._doc?.file_name || img.file_name || `Photo ${imgIdx + 1}`}
                                          >
                                            {img.question || img._doc?.file_name || img.file_name || `Photo ${imgIdx + 1}`}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Divider between images and files */}
                                {images.length > 0 && files.length > 0 && (
                                  <Box
                                    sx={{
                                      width: '2px',
                                      flexShrink: 0,
                                      alignSelf: 'stretch',
                                      backgroundColor: 'divider',
                                      mx: 0.75,
                                    }}
                                  />
                                )}

                                {/* Files - Right Side */}
                                {files.length > 0 && (
                                  <Box
                                    sx={{
                                      width: images.length > 0 ? 'calc(50% - 0.5px)' : '100%',
                                      flex: images.length > 0 ? '0 0 calc(50% - 0.5px)' : '1 1 100%',
                                      minWidth: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      minHeight: 0,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 0.5, mb: 0.5, width: '100%' }}>
                                      <AttachFile sx={{ fontSize: 14, color: 'primary.main', transform: 'rotate(45deg)' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Attachments ({files.length})
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        alignItems: 'flex-start',
                                        alignContent: 'flex-start',
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                        maxHeight: 100,
                                        overflowY: 'auto',
                                        pr: 0.5,
                                        '&::-webkit-scrollbar': { width: '3px' },
                                        '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' },
                                      }}
                                    >
                                      {files.map((file: any, fileIdx: number) => {
                                        const fileName = file._doc?.file_name || file.file_name || 'Unknown file';
                                        const ext = fileName.split('.').pop()?.toUpperCase() || 'FILE';
                                        return (
                                          <Box
                                            key={fileIdx}
                                            onClick={() => {
                                              const link = document.createElement('a');
                                              link.href = file.url;
                                              link.target = '_blank';
                                              link.download = fileName;
                                              link.click();
                                            }}
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              gap: 0.35,
                                              p: 0.5,
                                              width: 'fit-content',
                                              borderRadius: 1,
                                              backgroundColor: '#fff',
                                              border: '1px solid',
                                              borderColor: 'grey.200',
                                              cursor: 'pointer',
                                              transition: 'all 0.15s ease',
                                              '&:hover': {
                                                borderColor: 'primary.light',
                                                backgroundColor: 'primary.50',
                                              },
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 28,
                                                height: 28,
                                                alignSelf: 'center',
                                                borderRadius: 0.5,
                                                backgroundColor: ext === 'PDF' ? '#fee2e2' : '#e0e7ff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                              }}
                                            >
                                              
                                              <InsertDriveFileIcon sx={{ fontSize: 14, color: ext === 'PDF' ? '#dc2626' : '#4f46e5' }} />
                                            </Box>
                                            
                                            <Box sx={{ width: '100%', minWidth: 0, textAlign: 'left' }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize: '0.6rem',
                                                  fontWeight: 400,
                                                  color: 'text.primary',
                                                  display: 'block',
                                                  whiteSpace: 'normal',
                                                  wordBreak: 'break-word',
                                                  lineHeight: 1.2,
                                                }}
                                                title={fileName}
                                              >
                                                {fileName}
                                              </Typography>
                                              
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      </Grid>
                     )}

                     {showMediaSection && attachmentsLoading && (
                      <Grid item xs={12}>
                        <Box sx={{ px: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1, backgroundColor: '#fafbfc' }}>
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width={80} height={16} />
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Skeleton variant="rectangular" width={52} height={52} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" width={52} height={52} sx={{ borderRadius: 1 }} />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                     )}

                     {/* Inline Report Section */}
                    {canViewTransactionEmbed && showEmbedSection && (
                      <Grid item xs={12}>
                        <Box sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          mx: 0.5,
                          mb: 0.5,
                          padding: 0
                        }}>
                          {/* Header */}
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: 'primary.dark',
                          }}>
                            <Typography sx={{
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Transaction Report
                               {/* — {transactionDetails?.key} */}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setShowEmbedSection(false)}
                              sx={{ color: '#fff' }}
                            >
                              <HighlightOffIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>

                          {/* Content */}
                          {embedLoading ? (
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 200,
                              bgcolor: '#fafbfc'
                            }}>
                              <CircularProgress size={32} />
                            </Box>
                          ) : embedUrl ? (
                            <iframe
                              src={embedUrl}
                              frameBorder={0}
                              width="100%"
                              allowTransparency
                              style={{ border: 'none', display: 'block', minHeight: 400, height: 'auto' , margin: 0 }}
                            />
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 200,
                              bgcolor: '#fafbfc'
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                Unable to load report. Please try again.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} md={7}>
                      <Box sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>

                        {transactionDetails?.childrenHistory?.length > 0 &&
                          <Paper
                            elevation={0}
                            sx={{
                              background: 'white',
                              borderRadius: "10px",
                              marginBottom: "10px",
                              marginTop:"10px",
                              border: '1px solid',
                              borderColor: 'grey.200',
                              transition: 'all 0.2s ease-in-out',
                              overflow: 'hidden'
                            }}
                          >
                            {/* Header */}
                            <Box
                              sx={{
                                border: 'none',
                                outline: 'none',
                                color: '#fff',
                                px: 1,
                                py: 1.5,
                                backgroundColor: 'primary.dark',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Typography
                                fontWeight={600}
                                variant="h6"
                                sx={{
                                  color: '#fff',
                                  fontSize: '14px',
                                  letterSpacing: '0.025em',
                                }}
                              >
                                BOL NUMBERS
                              </Typography>

                              <CallSplitIcon sx={{ fontSize: 20, color: "#fff", marginRight: "4px", transform: "rotate(90deg)"}} />
                            </Box>

                            {/* Children List */}
                            {transactionDetails?.childrenHistory?.map((child: any, index: number) => (
                            <Box
                            key={index}
                            sx={{
                              px: 1,
                              py: 0.4,
                              borderBottom: '1px solid',
                              borderColor: 'grey.200',
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                          >
                            <Typography fontSize="13px" fontWeight={500} sx={{textTransform:'uppercase'}}>
                              {child?.key}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSelectedChild(child);
                                  setChildDialogOpen(true);
                                }}
                                sx={{
                                  fontSize: "11px",
                                  px: 1,
                                  py: 0.2,
                                  minWidth: 0,
                                  color: 'primary.dark',
                                  borderColor: 'primary.dark',
                                  textTransform: "none"
                                }}
                              >
                                View Transaction
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  navigate(`/transactions/details/${child?.team?.uid}/${child?.uid}/${child?.key}`);
                                }}
                                sx={{
                                  fontSize: "11px",
                                  px: 1,
                                  py: 0.2,
                                  minWidth: 0,
                                  bgcolor: 'primary.dark',
                                  textTransform: "none",
                                  "&:hover": {
                                    bgcolor: "#0f245c"
                                  }
                                }}
                              >
                                Edit Transaction
                              </Button>
                            </Box>

                          </Box>
                            ))}
                          </Paper>
                        }
                        
                        {/* Gated on the timeline tier's own state, not summary's
                            transactionDetails.history — those load independently
                            now, so using summary's data here would flash the
                            "no history" message while the timeline is still
                            in flight (or shown after it, but summary hasn't
                            resolved yet). */}
                        {(timelineLoading || timelineEvents.length > 0) ? (
                        <>
                          <Box sx={{ px: 0, py: 0 }}>
                            <Box
                              sx={{
                                border: 'none',
                                outline: 'none',
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                borderRight: '1px solid rgba(0,0,0,0.1)',
                                '& .MuiAccordionDetails-root': {
                                  borderTop: 'none'
                                }
                              }}
                            >
                              
                              
                        {timelineLoading && timelineEvents.length === 0 && (
                          <Box>
                            {[0, 1, 2].map((i) => (
                              <Paper
                                key={`timeline-skeleton-${i}`}
                                elevation={0}
                                sx={{
                                  borderRadius: '10px',
                                  mb: 2,
                                  border: '1px solid',
                                  borderColor: 'grey.200',
                                  overflow: 'hidden'
                                }}
                              >
                                <Skeleton variant="rectangular" height={32} />
                                <Box sx={{ p: 2 }}>
                                  <Skeleton variant="text" width="40%" />
                                  <Skeleton variant="text" width="70%" />
                                  <Skeleton variant="text" width="55%" />
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        )}
                        {timelineEvents.length > 0 ? (() => {
                          // timelineEvents is already newest-first (paginated
                          // tier), unlike the old single-fetch history array
                          // which needed a client-side reverse.
                          const histItems: any[] = (timelineEvents
                            ?.filter((item: any) => item.eventType === 'FORM' || item.eventType === 'INSTRUCTION' || item.eventType === 'REDIRECT')) || [];
                          const cards = histItems.map((item: any, idx: number, arr: any[]) => {
                            // Backend decorates each event with how its loop iteration should show:
                            //   iterationDisplay.number -> iteration count (only on the rule step;
                            //     null for the uncounted first fire and for in-between steps)
                            //   iterationDisplay.label  -> field value, carried forward to the steps
                            //     done after each counted rule fire.
                            const iterationDisplay = item?.iterationDisplay || {};
                            const iterationLabel = iterationDisplay.label || '';
                            // const showIterationNumber = iterationDisplay.number != null;
                            const showIterationNumber = false 
                            return (
                              <Fragment key={`transaction-item-${item._id || item.step?.title || idx}-${idx}`}>
                                {/* Iteration divider replaced by inline iteration number on the blue header below. */}
                                <Paper
                              elevation={0}
                              sx={{
                                background: 'white',
                                borderRadius: "10px",
                                mb: 2, // add space between cards
                                border: '1px solid',
                                borderColor: highlightedEventKey && eventKeyOf(item) === highlightedEventKey ? 'primary.main' : 'grey.200',
                                boxShadow: highlightedEventKey && eventKeyOf(item) === highlightedEventKey ? '0 0 0 3px rgba(19, 45, 118, 0.25)' : 'none',
                                transition: 'all 0.2s ease-in-out',
                                overflow: 'hidden',
                                scrollMarginTop: 120
                              }}
                              id={item?.step?.uid ? `step-${item.step.uid}` : undefined}
                              data-event-key={eventKeyOf(item) ? String(eventKeyOf(item)) : undefined}
                              onClick={() => {
                                getHistoryData(item);
                              }}
                            >

                              
                              {(item.step?.title || item.isDefaultStep || item.defaultStep) ? (
                                <Box
                                  sx={{
                                    p: "4px",
                                    backgroundColor: 'primary.dark',
                                    borderTopLeftRadius: 6,
                                    borderTopRightRadius: 6,
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    paddingLeft: "10px"
                                    
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      fontWeight={600}
                                      variant="subtitle1"
                                      sx={{
                                        color: '#fff',
                                        fontSize: '12px',
                                        letterSpacing: '0.025em',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      {item.step?.title || item.defaultStep?.title || 'Default Step'}
                                    </Typography>

                                    {/* Loop iteration indicator on the blue header.
                                        - Number: shown ONLY on the loop rule's source step (e.g. fourth step),
                                          and only from the 2nd (counted) rule fire onward.
                                        - Label (field value): carried forward onto the steps done after each
                                          counted rule fire. Both come pre-resolved from backend.iterationDisplay. */}
                                    {(showIterationNumber || iterationLabel) && (
                                      <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.5, ml: 1,
                                        bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.75, px: 0.75, py: 0.2,
                                      }}>
                                        {showIterationNumber && (
                                          <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>
                                            {iterationDisplay.number}
                                          </Typography>
                                        )}
                                        {iterationLabel && (
                                          <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 600, lineHeight: 1 }}>
                                            {iterationLabel}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}

                                    {item.detectionResults?.length > 0 && (
                                      <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.4,
                                        bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.75, px: 0.75, py: 0.2, ml: 1,
                                      }}>
                                        {/* Through detectionOutcome, so a manually resolved result
                                            colours this dot the same way it renders on the card. */}
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                                          bgcolor: item.detectionResults.every((d: any) => detectionOutcome(d) === 'true') ? '#4caf50'
                                            : item.detectionResults.some((d: any) => detectionOutcome(d) === 'false') ? '#f44336' : '#ff9800',
                                        }} />
                                        <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 600 }}>
                                          AI
                                        </Typography>
                                      </Box>
                                    )}

                                    {editingStepId === (item._id || item.id) && (
                                      <Box
                                        sx={{
                                          px: 1.5,
                                          py: 0.5,
                                          backgroundColor: 'rgba(255, 193, 7, 0.9)',
                                          borderRadius: 1,
                                          ml: 1
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#000',
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                          }}
                                        >
                                          EDITING
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  
                                  {/* Edit/Save/Cancel buttons */}
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', }}>
                                    {(() => {
                                      const updatedName = (() => {
                                        // Prefer explicit updatedBy object
                                        if (typeof item?.updatedBy === 'object' && item.updatedBy !== null) {
                                          if (item.updatedBy?.firstName || item.updatedBy?.lastName) return `${item.updatedBy?.firstName || ''} ${item.updatedBy?.lastName || ''}`.trim();
                                          if (item.updatedBy?.name) return item.updatedBy.name;
                                        }

                                        // If updatedBy is a string and matches current user id, use user info
                                        if (typeof item?.updatedBy === 'string') {
                                          if (item.updatedBy === item?.user?.id) {
                                            if (item?.user?.firstName || item?.user?.lastName) return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                            if (item?.user?.name) return item.user.name;
                                          }
                                          return 'Updated User';
                                        }

                                        // Fallbacks (preserve original behavior)
                                        if (item?.user?.firstName || item?.user?.lastName) return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                        if (item?.user?.name) return item.user.name;
                                        if (item?.createdBy?.firstName || item?.createdBy?.lastName) return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                        if (item?.createdBy?.name) return item.createdBy.name;
                                        return '';
                                      })();

                                      const updatedDate = (() => {
                                        console.log('Attempting to parse date for item:', item?.updatedAt);
                                        try {
                                          const dateValue = item?.updatedAt || item?.updated_at || item?.createdAt || item?.timestamp || item?.date || item?.created_at;
                                          return dateValue ? formatTimestampWithLocalTimezone(dateValue) : '';
                                        } catch (e) {
                                          return '';
                                        }
                                      })();

                                      if (!updatedName && !updatedDate) return null;
                                      return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', color: '#fff', mr: 1, minWidth: 0 }}>
                                          <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Box component="span" sx={{fontSize: '0.7rem', color: '#fff', fontWeight: 400, textTransform: 'uppercase', mr: 0.5 }}>Updated By</Box>
                                            <Box component="span" sx={{ fontWeight: 500, textTransform: 'none', ml: 0.5 }}>{updatedName}</Box>
                                            {/* Jumio state for whoever the name resolved to. Renders nothing
                                                for dashboard-only roles, which have no licence field. */}
                                            <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
                                              <JumioVerificationBadge
                                                onDark
                                                verification={verificationFor(
                                                  verificationMap,
                                                  typeof item?.updatedBy === 'object' ? item?.updatedBy?.uid : undefined,
                                                  typeof item?.updatedBy === 'object' ? item?.updatedBy?._id : undefined,
                                                  item?.user?.uid,
                                                  item?.user?._id
                                                )}
                                              />
                                            </Box>
                                          </Typography>
                                          {updatedDate && (
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff' }}>
                                              {updatedDate}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })()}
                                    {editingStepId === (item._id || item.id) ? (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEdit();
                                          }}
                                          disabled={saving}
                                          sx={{
                                            color: '#fff',
                                            bgcolor: 'rgba(244, 67, 54, 0.2)',
                                            border: '1px solid rgba(244, 67, 54, 0.3)',
                                            '&:hover': {
                                              bgcolor: 'rgba(244, 67, 54, 0.3)',
                                            }
                                          }}
                                          title="Cancel Edit"
                                        >
                                          <Cancel sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        {/* Info icon beside Edit (interactive tooltip showing same content as footer) */}
                                            <Tooltip
                                              placement="bottom"
                                              arrow
                                              componentsProps={{ tooltip: { sx: { bgcolor: 'transparent', boxShadow: 'none',  } }, arrow: { sx: { color: 'transparent' } } }}
                                              title={
                                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 'none' ,border: '1px solid rgba(0, 0, 0, 0.12)'}}>
                                                  <Grid container spacing={1.5} sx={{  flexDirection: 'column', display:"flex" }}>
                                                    <Grid item xs={12}  >
                                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                                                        {(() => {
                                                          const createdByName = (() => {
                                                            if (item?.user?.firstName || item?.user?.lastName) {
                                                              return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                                            } else if (item?.user?.name) {
                                                              return item.user.name;
                                                            } else if (item?.userName) {
                                                              return item.userName;
                                                            } else if (item?.createdBy?.firstName || item?.createdBy?.lastName) {
                                                              return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                                            } else if (item?.createdBy?.name) {
                                                              return item.createdBy.name;
                                                            }
                                                            return 'Not Available';
                                                          })();

                                                          const submittedTime = (() => {
                                                            try {
                                                              const dateValue = item?.updateTime || item?.createdAt;
                                                              return dateValue ? formatTimestampWithLocalTimezone(dateValue) : 'Not Available';
                                                            } catch (error) {
                                                              return 'Not Available';
                                                            }
                                                          })();

                                                          const createdTime = (() => {
                                                            try {
                                                              const dateValue = item?.createdAt;
                                                              return dateValue ? formatTimestampWithLocalTimezone(dateValue) : 'Not Available';
                                                            } catch (error) {
                                                              return 'Not Available';
                                                            }
                                                          })();

                                                          return (
                                                            <>
                                                        <Typography                                                                                               
                                                          variant="body2"
                                                          sx={{
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            fontWeight: 500,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                          }}
                                                        >
                                                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}>
                                                            Created By:{' '}
                                                          </Box>
                                                          {createdByName}
                                                        </Typography>

                                                        {(item?.user?.resourceTypeId?.roleName || item?.user?.resourceTypeId?.name || (typeof item?.user?.resourceTypeId === 'string' && item?.user?.resourceTypeId)) && (
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              fontSize: '0.7rem',
                                                              color: 'text.secondary',
                                                              fontWeight: 500,
                                                              overflow: 'hidden',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              mt: 0.25
                                                            }}
                                                          >
                                                            ({item.user.resourceTypeId.name ? item.user.resourceTypeId.name : item.user.resourceTypeId.roleName})
                                                          </Typography>
                                                        )}

                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                            fontWeight: 500,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            mt: 0.25
                                                          }}
                                                        >
                                                          Uploaded: {createdTime}
                                                        </Typography>

                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                            fontWeight: 500,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            mt: 0.25
                                                          }}
                                                        >
                                                          Submitted: {submittedTime}
                                                        </Typography>
                                                            </>
                                                          );
                                                        })()}
                                                      </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={12}>
                                                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                        <Typography
                                                          fontWeight={600}
                                                          variant="caption"
                                                          sx={{
                                                            color: 'primary.main',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            fontSize: '0.65rem',
                                                            mb: 0.5
                                                          }}
                                                        >
                                                          Location
                                                        </Typography>
                                                        {(() => {
                                                          const hasValidCoordinates =
                                                            item?.location?.coordinates &&
                                                            Array.isArray(item.location.coordinates) &&
                                                            item.location.coordinates.length >= 2 &&
                                                            !isNaN(Number(item.location.coordinates[0])) &&
                                                            !isNaN(Number(item.location.coordinates[1]));

                                                          if (hasValidCoordinates) {
                                                            return (
                                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                                                                <Box
                                                                  sx={{
                                                                    height: 32,
                                                                    width: 32,
                                                                    borderRadius: 0.75,
                                                                    border: '1px solid',
                                                                    borderColor: 'grey.200',
                                                                    bgcolor: 'grey.50',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0
                                                                  }}
                                                                >
                                                                  <LocationOnIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                                                </Box>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                  <Typography
                                                                    variant="body2"
                                                                    sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, mb: 0.25, wordBreak: 'break-word' }}
                                                                  >
                                                                    {item.location?.location || item.location?.address || 'Location Available'}
                                                                  </Typography>
                                                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1, fontFamily: 'monospace' }}>
                                                                    {`${Number(item.location.coordinates[0]).toFixed(4)}, ${Number(item.location.coordinates[1]).toFixed(4)}`}
                                                                  </Typography>
                                                                </Box>
                                                              </Box>
                                                            );
                                                          } else if (item?.location?.location || item?.location?.address || item?.locationText) {
                                                            return (
                                                              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, wordBreak: 'break-word', width: '100%' }}>
                                                                {item.location?.location || item.location?.address || item.locationText}
                                                              </Typography>
                                                            );
                                                          }
                                                          return (
                                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, fontStyle: 'italic' }}>
                                                              Not Available
                                                            </Typography>
                                                          );
                                                        })()}
                                                      </Box>
                                                    </Grid>

                                                     
                                                  </Grid>
                                                </Box>
                                              }
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  console.log('Info clicked for item:', item);
                                                }}
                                                sx={{
                                                  color: '#fff',
                                                  bgcolor: 'rgba(255, 255, 255, 0.06)',
                                                  border: '1px solid rgba(255, 255, 255, 0.12)',
                                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', transform: 'scale(1.05)' },
                                                  transition: 'all 0.2s ease-in-out'
                                                }}
                                                title="Info"
                                              >
                                                <InfoOutlined sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Tooltip>

                                        {/* Only show Edit button if user has access to edit this step */}
                                        {canEditStep(item) && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                console.log('Edit button clicked for item:', item);
                                                console.log('Item _id:', item._id);
                                                handleEditStep(item);
                                              }}
                                              sx={{
                                                color: '#fff',
                                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                '&:hover': {
                                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                  transform: 'scale(1.05)'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                              }}
                                            >
                                              <Edit sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {!item.isRedo && !item.isReset && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRedoStep(item);

                                              }}
                                              disabled={saving}
                                              sx={{
                                                color: '#fff',
                                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                '&:hover': {
                                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                  transform: 'scale(1.05)'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                              }}
                                              title="Reset Step"
                                            >
                                              <Redo sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          )}
                                          {(item.isRedo || item.isReset) && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#ffffffff',
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                backgroundColor: 'rgba(122, 124, 122, 0.1)',
                                                px: 0.4,
                                                py: 0.5,
                                                borderRadius: 1,
                                                border: '1px solid rgba(255, 255, 255, 0.94)',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                            Restarted Successfully
                                            </Typography>
                                          )}
                                        </Box>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              ) : null}
                              <Grid container spacing={0.3} sx={{mt:0.3, pl:0.3}}>
                                {/* INSTRUCTION answers are written into the signed
                                    document, which is shown below — listing them
                                    again would duplicate the record. They are still
                                    stored in data[] for reports, rules and search. */}
                                {item?.data?.length && item.eventType !== 'INSTRUCTION' ?
                                  item?.data?.map((fields: any, fieldIndex: number) => (
                                    (() => {
                                      const fieldSchema = getFieldSchema(item, fields);
                                      const isCounterField = fields.inputComponent === 'TEXT' && Boolean(fieldSchema?.settings?.isCounter);
                                      const counterValue = isCounterField ? getCommaSeparatedCount(fields.value?.value) : 0;

                                      return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={4}
                                      key={`field-${fieldIndex}-${fields.question || fieldIndex}`}
                                      sx={{ p: 0.5,  }}>
                                      <Box
                                        sx={{
                                          border: '1px solid',
                                          borderColor: '#d5d5d5',
                                          borderRadius: 1,
                                          background: (fields.value && (fields.value.value || (Array.isArray(fields.value.files) && fields.value.files.length > 0))) ? '#ffffff' : 'transparent',
                                          height: '100%',
                                          boxSizing: 'border-box',
                                          p: 0
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            m: '2px 5px 0px 5px',
                                          }}
                                        >
                                          <Typography
                                            fontWeight={600}
                                            variant="body2"
                                            sx={{
                                              color: 'text.primary',
                                              fontSize: '0.8rem',
                                              letterSpacing: '0.025em',
                                              whiteSpace: 'normal',
                                              flex: 1,
                                            }}
                                          >
                                            {toSentenceCase(fields.question)}
                                          </Typography>
                                          {isCounterField && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'primary.main',
                                                fontWeight: 700,
                                                fontSize: '0.72rem',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              Count: {counterValue}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Box sx={{     }}>
                                          {((Array.isArray(fields.value?.files) && fields.value?.files.length > 0) || Array.isArray(fields.value?.value)) && fields.inputComponent === 'SWITCH' ? 
                                          fields.inputComponent === 'SWITCH' ? (
                                                  <>
                                                    {/* Display files if present */}
                                                    {Array.isArray(fields.value?.files) && fields.value?.files.length > 0 && (
                                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5, ml: '5px' }}>
                                                        {fields.value.files.map((img: any, imgIndex: number) => {
                                                          const ext = img.file_name?.split('.').pop()?.toLowerCase() || '';
                                                          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                                                          const isVideo = ext === 'mp4';
                                                          return (
                                                            <Box
                                                              key={`file-switch-${fieldIndex}-${imgIndex}-${img?._id || img?.url}`}
                                                              sx={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                border: '1.5px solid',
                                                                borderColor: 'grey.200',
                                                                transition: 'all 0.2s ease-in-out',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: isImage ? 'transparent' : 'grey.50',
                                                                p: '4px',
                                                                '&:hover': {
                                                                  borderColor: 'primary.main',
                                                                  transform: 'scale(1.05)'
                                                                  
                                                                }
                                                              }}
                                                            >
                                                              {img?.url ? (
                                                                isImage || isVideo ? (
                                                                  <img
                                                                    src={isVideo ? video : img?.url}
                                                                    alt={img.file_name}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                    onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      if (isVideo) {
                                                                        setVideoUrl(img.url);
                                                                        setShowVideo(true);
                                                                      } else if (isImage) {
                                                                        const fieldImages = fields.value.files
                                                                          .filter((file: any) => {
                                                                            const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                          })
                                                                          .map((file: any) => ({ src: file.url }));
                                                                        setImagesPreview(fieldImages);
                                                                        const imageIndex = fields.value.files
                                                                          .filter((file: any) => {
                                                                            const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                          })
                                                                          .findIndex((file: any) => file.url === img.url);
                                                                        setCurrentImage(imageIndex >= 0 ? imageIndex : 0);
                                                                        setViewerIsOpen(true);
                                                                      }
                                                                    }}
                                                                  />
                                                                ) : (
                                                                <Tooltip title={img.file_name || 'Document'} arrow>
                                                                  <Box
                                                                    sx={{
                                                                      display: 'flex',
                                                                      flexDirection: 'column',
                                                                      alignItems: 'center',
                                                                      justifyContent: 'center',
                                                                      p: "5px",
                                                                      textAlign: 'center',
                                                                      width: '100%',
                                                                      height: '100%'
                                                                    }}
                                                                    onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      window.open(img.url, '_blank');
                                                                    }}
                                                                  >
                                                                    <InsertDriveFileIcon
                                                                      sx={{
                                                                        fontSize: 20,
                                                                        color: 'primary.main',
                                                                        mb: "3px"
                                                                      }}
                                                                    />
                                                                    <Typography
                                                                      sx={{
                                                                        fontSize: '6px',
                                                                        color: 'text.secondary',
                                                                        fontWeight: 500,
                                                                        textTransform: 'uppercase',
                                                                        mb: "2px"
                                                                      }}
                                                                    >
                                                                      {ext || 'FILE'}
                                                                    </Typography>
                                                                    <Typography
                                                                      sx={{
                                                                        fontSize: '10px',
                                                                        color: 'text.secondary',
                                                                        fontWeight: 400,
                                                                        width: '100%',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        px: '2px'
                                                                      }}
                                                                    >
                                                                      {img.file_name || 'Document'}
                                                                    </Typography>
                                                                  </Box>
                                                                </Tooltip>
                                                              )
                                                              ) : null}
                                                            </Box>
                                                          );
                                                        })}
                                                      </Box>
                                                    )}
                                                    {/* Display values */}
                                                    {Array.isArray(fields.value.value)
                                                      ? fields.value.value.map((val: any, valIndex: number) => (
                                                          <Typography
                                                            key={`switch-value-${fieldIndex}-${valIndex}`}
                                                            variant="body2"
                                                            sx={{
                                                              fontSize: '0.8rem',
                                                              color: 'text.secondary',
                                                              lineHeight: 1.5,
                                                              mb: 0.5,
                                                              ml: '5px' /* match question margin */
                                                            }}
                                                          >
                                                          {/* 1 {val || 'NA'}
                                                            */}  {val === true || val === "true"
                                                          ? "Yes"
                                                          : val === false || val === "false"
                                                          ? "No"
                                                          : "NA"}
                                                          </Typography>
                                                        ))
                                                      : (
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            fontSize: '0.8rem',
                                                            color: 'text.secondary',
                                                            lineHeight: 1.5,
                                                            mb: 0.5,
                                                            ml: '5px' /* match question margin */
                                                          }}
                                                        >
                                                          {fields.value.value || 'NA'}
                                                        </Typography>
                                                      )
                                                    }
                                                  </>): (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                              
                                              {fields?.value?.files.map((img: any, imgIndex: number) => {
                                                const ext = img.file_name?.split('.').pop()?.toLowerCase() || '';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                                                const isVideo = ext === 'mp4';

                                                return (
                                                  <Box key={`file-${fieldIndex}-${imgIndex}-${img?._id || img?.url}`} sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    maxWidth: 70  // Slightly wider to accommodate filename
                                                  }}>
                                                    <Box

                                                      sx={{
                                                        width: 70,
                                                        height: 70,
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        border: '1.5px solid',
                                                        borderColor: 'grey.200',
                                                        transition: 'all 0.2s ease-in-out',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: isImage ? 'transparent' : 'grey.50',
                                                        p: '4px',
                                                        '&:hover': {
                                                          borderColor: 'primary.main',
                                                          transform: 'scale(1.05)'
                                                        }
                                                      }}
                                                    >
                                                      {img?.url ? (
                                                        isImage || isVideo ? (
                                                          <img
                                                            src={isVideo ? video : img?.url}
                                                            alt={img.file_name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              if (isVideo) {
                                                                setVideoUrl(img.url);
                                                                setShowVideo(true);
                                                              } else if (isImage) {
                                                                // Prepare images for this specific field
                                                                const fieldImages = fields?.value?.files
                                                                  .filter((file: any) => {
                                                                    const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                  })
                                                                  .map((file: any) => ({ src: file.url, question: fields.question }));

                                                                setImagesPreview(fieldImages);
                                                                // Find the correct index in the filtered images array
                                                                const imageIndex = fields.value.files
                                                                  .filter((file: any) => {
                                                                    const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                  })
                                                                  .findIndex((file: any) => file.url === img.url);

                                                                setCurrentImage(imageIndex >= 0 ? imageIndex : 0);
                                                                setViewerIsOpen(true);
                                                              }
                                                            }}
                                                          />
                                                        ) : (
                                                          <Tooltip title={img.file_name || 'Document'} arrow>
                                                            <Box
                                                              sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                p: "5px",
                                                                textAlign: 'center',
                                                                width: '70px',
                                                                height: '70px'
                                                              }}
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(img.url, '_blank');
                                                              }}
                                                            >
                                                              <InsertDriveFileIcon
                                                                sx={{
                                                                  fontSize: 20,
                                                                  color: 'primary.main',
                                                                  mb: "3px"
                                                                }}
                                                              />
                                                              <Typography
                                                                sx={{
                                                                  fontSize: '6px',
                                                                  color: 'text.secondary',
                                                                  fontWeight: 500,
                                                                  textTransform: 'uppercase',
                                                                  mb: "2px"
                                                                }}
                                                              >
                                                                {ext || 'FILE'}
                                                              </Typography>
                                                              <Typography
                                                                sx={{
                                                                  fontSize: '10px',
                                                                  color: 'text.secondary',
                                                                  fontWeight: 400,
                                                                  width: '100%',
                                                                  overflow: 'hidden',
                                                                  textOverflow: 'ellipsis',
                                                                  whiteSpace: 'nowrap',
                                                                  px: '2px'
                                                                }}
                                                              >
                                                                {img.file_name || 'Document'}
                                                              </Typography>
                                                            </Box>
                                                          </Tooltip>
                                                        )
                                                      ) : null}
                                                    </Box>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          ) : (Array.isArray(fields.value?.files) && fields.value?.files.length > 0) ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                              {fields?.value?.files.map((img: any, imgIndex: number) => {
                                                const ext = img.file_name?.split('.').pop()?.toLowerCase() || '';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                                                const isVideo = ext === 'mp4';

                                                return (
                                                  <Box
                                                    key={`file-${fieldIndex}-${imgIndex}-${img?._id || img?.url}`}
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                      maxWidth: 70
                                                    }}
                                                  >
                                                    {/* The 50x50 icon box */}
                                                    <Box
                                                      sx={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        border: '1.5px solid',
                                                        borderColor: 'grey.200',
                                                        transition: 'all 0.2s ease-in-out',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: isImage ? 'transparent' : 'grey.50',
                                                        p: '4px',
                                                        '&:hover': {
                                                          borderColor: 'primary.main',
                                                          transform: 'scale(1.05)'
                                                        }
                                                      }}
                                                    >
                                                      {img?.url ? (
                                                        isImage || isVideo ? (
                                                          isVideo ? (
                                                            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                                              <video
                                                                src={img.url}
                                                                style={{ 
                                                                  width: '100%', 
                                                                  height: '100%', 
                                                                  objectFit: 'cover',
                                                                  display: 'block'
                                                                }}
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setVideoUrl(img.url);
                                                                  setShowVideo(true);
                                                                }}
                                                              />
                                                              <Box
                                                                sx={{
                                                                  position: 'absolute',
                                                                  top: '50%',
                                                                  left: '50%',
                                                                  transform: 'translate(-50%, -50%)',
                                                                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                                                                  borderRadius: '50%',
                                                                  width: 24,
                                                                  height: 24,
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  justifyContent: 'center',
                                                                  cursor: 'pointer',
                                                                  '&:hover': {
                                                                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                                                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                                                  },
                                                                  transition: 'all 0.2s ease'
                                                                }}
                                                              >
                                                                <PlayArrowIcon 
                                                                  sx={{ 
                                                                    color: 'white',
                                                                    fontSize: 16
                                                                  }} 
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setVideoUrl(img.url);
                                                                    setShowVideo(true);
                                                                  }}
                                                                />
                                                              </Box>
                                                            </Box>
                                                          ) : (
                                                            <img
                                                              src={img?.url}
                                                              alt={img.file_name}
                                                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fieldImages = fields?.value?.files
                                                                  .filter((file: any) => {
                                                                    const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                  })
                                                                  .map((file: any) => ({ src: file.url,question: fields.question,  }));

                                                                setImagesPreview(fieldImages);
                                                                const imageIndex = fields.value.files
                                                                  .filter((file: any) => {
                                                                    const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                  })
                                                                  .findIndex((file: any) => file.url === img.url);

                                                                setCurrentImage(imageIndex >= 0 ? imageIndex : 0);
                                                                setViewerIsOpen(true);
                                                              }}
                                                            />
                                                          )
                                                        ) : (
                                                          <Box
                                                            sx={{
                                                              display: 'flex',
                                                              flexDirection: 'column',
                                                              alignItems: 'center',
                                                              justifyContent: 'center',
                                                              p: 0.5,
                                                              textAlign: 'center',
                                                              width: '100%',
                                                              height: '100%'
                                                            }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              window.open(img.url, '_blank');
                                                            }}
                                                          >
                                                            <InsertDriveFileIcon
                                                              sx={{
                                                                fontSize: 24,
                                                                color: 'primary.main',
                                                                mb: 0.5
                                                              }}
                                                            />
                                                            <Typography
                                                              sx={{
                                                                fontSize: '6px',
                                                                color: 'text.secondary',
                                                                fontWeight: 500,
                                                                textTransform: 'uppercase'
                                                              }}
                                                            >
                                                              {ext || 'FILE'}
                                                            </Typography>
                                                          </Box>
                                                        )
                                                      ) : null}
                                                    </Box>
                                                    
                                                    {/* Filename OUTSIDE the border - only for documents */}
                                                    {!isImage && !isVideo && (
                                                      <Tooltip title={img.file_name || 'Document'} arrow placement="bottom">
                                                        <Typography
                                                          sx={{
                                                            fontSize: '10px',
                                                            color: 'text.secondary',
                                                            fontWeight: 400,
                                                            width: '100%',
                                                            maxWidth: 70,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'center',
                                                            cursor: 'pointer'
                                                          }}
                                                        >
                                                          {img.file_name || 'Document'}
                                                        </Typography>
                                                      </Tooltip>
                                                    )}
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          ) : fields?.value?.value ? (
                                            fields.inputComponent === 'RESOURCE' && Array.isArray(fields.value.value) ? (
                                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1,  px:0.5}}>
                                                {fields.value.value
                                                  .filter((resource: any) => resource && typeof resource === 'object')
                                                  .map((resource: any, resourceIdx: number) => (
                                                  <Paper
                                                    key={resource.resourceDataId || resource.id || resourceIdx}
                                                    sx={{
                                                      p: "5px",
                                                      
                                                      bgcolor: 'grey.100', 
                                                      cursor: 'pointer',
                                                      border: '1px solid',
                                                      borderColor: 'primary.light',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 1,
                                                      '&:hover': { boxShadow: 2, borderColor: 'primary.main' }
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      // Use local resource data instead of making an API call
                                                      setResourceDialogData(resource);
                                                      setResourceDialogOpen(true);
                                                    }}
                                                  >
                                                    {/* <InfoOutlined sx={{ color: 'primary.main',  }} /> */}
                                                    {/* Jumio state for the person named on this step. Looked
                                                        up live (verificationMap), never off this snapshot —
                                                        the snapshot froze when the step was submitted. */}
                                                    <JumioVerificationBadge
                                                      verification={verificationFor(
                                                        verificationMap,
                                                        resource?.resourceDataId,
                                                        resource?.id,
                                                        resource?._id,
                                                        resource?.uid
                                                      )}
                                                    />
                                                    <Box  sx={{
                                                          fontWeight: 500,
                                                          fontSize: 13,

                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                          flex: 1
                                                        }}>
                                                      {resource?.fields && Object.keys(resource?.fields || {}).length > 0 ? (
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13,overflow:'hidden', textOverflow: 'ellipsis' }}>
                                                          {Object.values(resource.fields)
                                                            .filter((value, index) => {
                                                              const keys = Object.keys(resource.fields);
                                                              const keyName = keys[index];
                                                              const valStr = String(value || '').trim();
                                                              
                                                              // 1. Filter out by specific key names
                                                              if (['countryCode', 'team', 'id', '_id','Notes'].includes(keyName)) return false;

                                                              // 2. Filter out objects (image fields etc.) — rendered as thumbnails separately
                                                              if (value !== null && typeof value === 'object' && !Array.isArray(value)) return false;

                                                              // 3. Filter out emails
                                                              const isEmail = valStr.includes('@');
                                                              if (isEmail) return false;

                                                              // 4. Filter out technical identifiers (Arrays, MongoDB IDs, UUIDs)
                                                              const isTechnicalId =
                                                                Array.isArray(value) ||
                                                                /^[0-9a-fA-F]{24}$/.test(valStr) ||
                                                                /^[0-9a-fA-F-]{36}$/.test(valStr);
                                                              if (isTechnicalId) return false;

                                                              // 5. Final check: ensure value is not empty
                                                              return valStr !== '';
                                                            })
                                                            .map((value, idx, arr) => (
                                                              <span key={idx}>
                                                                {String(value)}
                                                                {idx < arr.length - 1 ? '- ' : ''}
                                                              </span>
                                                            ))}
                                                        </Typography>
                                                      ) : (
                                                        <Typography variant="body2" color="text.secondary">No details</Typography>
                                                      )}
                                                    </Box>
                                                  </Paper>
                                                ))}
                                              </Box>
                                            ) : Array.isArray(fields.value.value) && fields.value.value.length > 0 && fields.value.value.some((v: any) => v) ? (
                                              fields.value.value.map((val: string, valIndex: number) => (
                                                <Typography
                                                  key={`value-${fieldIndex}-${valIndex}`}
                                                  variant="body2"
                                                  sx={{
                                                    fontSize: '0.8rem',
                                                    color: 'text.secondary',
                                                    lineHeight: 1, 
                                                    p:0.5, 
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                  }}
                                                >
                                                  {val
                                                    ? fields.inputComponent === "DATE"
                                                      ? formatToLocalTime(val)
                                                      : fields.inputComponent === 'TEXT'
                                                      ? String(val)
                                                          .split(',')
                                                          .map((part) => part.trim())
                                                          .filter(Boolean)
                                                          .join(', ')
                                                      : formatDynamicScalar(val)
                                                    : '   NA'}
                                                </Typography>
                                              ))
                                            ):fields.inputComponent === 'ESIGN'?
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                              {fields?.value?.files.length > 0 ? fields?.value?.files.map((img: any, imgIndex: number) => {
                                                const ext = img.file_name?.split('.').pop()?.toLowerCase() || '';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                                                const isVideo = ext === 'mp4';

                                                return (
                                                  <Box
                                                    key={`file-${fieldIndex}-${imgIndex}-${img?._id || img?.url}`}
                                                    sx={{
                                                      width: 50,
                                                      height: 50,
                                                      borderRadius: 1,
                                                      overflow: 'hidden',
                                                      cursor: 'pointer',
                                                      border: '1.5px solid',
                                                      borderColor: 'grey.200',
                                                      transition: 'all 0.2s ease-in-out',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      bgcolor: '#fff',
                                                      p: '4px',
                                                      '&:hover': {
                                                        borderColor: 'primary.main',
                                                        transform: 'scale(1.05)'
                                                      }
                                                    }}
                                                  >
                                                    {img?.url ? (
                                                      isImage || isVideo ? (
                                                        <img
                                                          src={isVideo ? video : img?.url}
                                                          alt={img.file_name}
                                                          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                                          onClick={(e) => {
                                                            e.stopPropagation(); // Prevent triggering parent onClick
                                                            if (isVideo) {
                                                              setVideoUrl(img.url);
                                                              setShowVideo(true);
                                                            } else if (isImage) {
                                                              // Prepare images for this specific field
                                                              const fieldImages = fields?.value?.files
                                                                .filter((file: any) => {
                                                                  const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                })
                                                                .map((file: any) => ({ src: file.url }));

                                                              setImagesPreview(fieldImages);
                                                              // Find the correct index in the filtered images array
                                                              const imageIndex = fields.value.files
                                                                .filter((file: any) => {
                                                                  const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                                  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                                })
                                                                .findIndex((file: any) => file.url === img.url);

                                                              setCurrentImage(imageIndex >= 0 ? imageIndex : 0);
                                                              setViewerIsOpen(true);
                                                            }
                                                          }}
                                                        />
                                                      ) : (
                                                        <Box
                                                          sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            p: "10px",
                                                            textAlign: 'center'
                                                          }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            // For non-image files, you could open in new tab or download
                                                            window.open(img.url, '_blank');
                                                          }}
                                                        >
                                                          <InsertDriveFileIcon
                                                            sx={{
                                                              fontSize: 20,
                                                              color: 'primary.main',
                                                              mb: "10px"
                                                            }}
                                                          />
                                                          <Typography
                                                            sx={{
                                                              fontSize: '6px',
                                                              color: 'text.secondary',
                                                              fontWeight: 500,
                                                              textTransform: 'uppercase'
                                                            }}
                                                          >
                                                            {ext || 'FILE'}
                                                          </Typography>
                                                        </Box>
                                                      )
                                                    ) : "NA"
                                                  }
                                                </Box>
                                              );
                                            }):<Typography sx={{
                                                             fontSize: '0.8rem',
                                                            color: 'text.secondary',
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            ml: '5px' /* match question margin */
                                                            }}>NA</Typography>}
                                            </Box> : Array.isArray(fields.value.value) ? (
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5, mb: 0.5, ml: '5px' /* match question margin */ }}>
                                                NA
                                              </Typography>
                                            ) : (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.8rem',
                                                  color: 'text.secondary',
                                                  lineHeight: 1.5,
                                                  px: 1,
                                                  width: '100%',
                                                  boxSizing: 'border-box',
                                                  whiteSpace: 'normal',
                                                  wordBreak: 'break-word',
                                                  overflowWrap: 'anywhere',
                                                }}
                                              >
                                                {/* Only a DATE field goes through the date formatter. This used to
                                                    send EVERY type except TEXT and CAMERA through it, so a NUMBER
                                                    field's value was read as an epoch-millisecond timestamp and
                                                    rendered "01 Jan 1970" for any small number. */}
                                                {fields.inputComponent === 'TEXT'
                                                  ? String(fields.value.value ?? '')
                                                      .split(',')
                                                      .map((part) => part.trim())
                                                      .filter(Boolean)
                                                      .join(', ')
                                                  : fields.inputComponent === 'DATE'
                                                  ? formatToLocalTime(fields.value.value)
                                                  : fields.inputComponent === 'CAMERA'
                                                  ? fields?.value?.value
                                                  : formatDynamicScalar(fields.value.value)}
                                              </Typography>
                                            )
                                          ) : (
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5, mb: 0.5  , ml: '5px'}}>
                                              NA
                                            </Typography>
                                          )}
                                        </Box>
                                        {(() => {
                                          const fieldHistory: FieldHistoryEntry[] = (item?.previousData || [])
                                            .map((pd: any) => {
                                              const change = pd?.changedFields?.find((cf: any) => cf?.field === fields.field);
                                              return change ? { editedAt: pd?.editedAt, editedBy: pd?.editedBy, change } : null;
                                            })
                                            .filter(Boolean)
                                            .sort((a: any, b: any) => new Date(b.editedAt).getTime() - new Date(a.editedAt).getTime());

                                          return (
                                            <FieldPreviousValuesAccordion
                                              history={fieldHistory}
                                              currentInputComponent={fields.inputComponent}
                                              fieldKey={fields.field}
                                              onOpenImages={(imgs, idx) => {
                                                setImagesPreview(imgs);
                                                setCurrentImage(idx);
                                                setViewerIsOpen(true);
                                              }}
                                              onOpenVideo={(url) => {
                                                setVideoUrl(url);
                                                setShowVideo(true);
                                              }}
                                            />
                                          );
                                        })()}
                                      </Box>
                                    </Grid>
                                      );
                                    })()
                                  )):((item?.eventType === "INSTRUCTION"&&item?.files?.length==0) ||
                                      /* A REDIRECT that captured nothing is a 'home' exit — that mode has no
                                         destination to record. The other modes always submit a key, and the
                                         backend records no event when one fails to resolve, so an empty
                                         data[] on a REDIRECT can only mean home. */
                                      item?.eventType === "REDIRECT") && (
                                     <Typography sx={{ml:1 , mt: 2,   whiteSpace: 'normal',   wordBreak: 'break-word',overflow: 'visible',}}>
                                          {item?.eventType === "REDIRECT" ? "Acknowledged by " : "Instruction has been Acknowledged by "}<b>
                                      {(() => { 
                                        let userName = "Unknown User";

                                        if (typeof item?.updatedBy === "object" && item?.updatedBy !== null) {
                                          if (item.updatedBy.firstName || item.updatedBy.lastName) {
                                            userName = `${item.updatedBy.firstName || ""} ${item.updatedBy.lastName || ""}`.trim();
                                          } else if (item.updatedBy.name) {
                                            userName = item.updatedBy.name;
                                          }
                                        } else if (typeof item?.updatedBy === "string") {
                                          if (item.updatedBy === item?.user?.id) {
                                            userName =
                                              `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim() ||
                                              item.user?.name ||
                                              "Current User";
                                          } else {
                                            userName = "Updated User";
                                          }
                                        }

                                        return `${userName}`;
                                      })()}
                                    </b>
                                  </Typography>
                                )}
                                  {item?.files?.length > 0 && (
                                    <Grid item xs={12} sm={3}>
                                      <Box
                                        sx={{
                                          p: 1,
                                          borderRadius: 1,
                                          bgcolor: 'grey.50',
                                          border: '1px solid',
                                          borderColor: 'grey.100'
                                        }}
                                      >
                                        <Typography
                                          fontWeight={600}
                                          variant="body2"
                                          sx={{
                                            color: 'text.primary',
                                            mb: 1,
                                            fontSize: '0.8rem',
                                            letterSpacing: '0.025em',
                                            pt: 0.5,
                                          }}
                                        >
                                          {item.eventType === 'INSTRUCTION' ? 'Signed document' : 'Files'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                          {item?.files?.map((img: any, attachmentIndex: number) => {
                                            const ext = img.file_name?.split('.').pop()?.toLowerCase() || '';
                                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                                            const isVideo = ext === 'mp4';
                                            // Thumbnail the signed document, so the tile shows what was
                                            // signed rather than a generic icon. Scoped to INSTRUCTION:
                                            // rendering every PDF in the UI would mean downloading
                                            // customer source documents, which can be many MB.
                                            const isSignedPdf = ext === 'pdf' && item.eventType === 'INSTRUCTION';

                                            return (
                                              <Box
                                                key={`attachment-${idx}-${attachmentIndex}-${img._id || img.url || attachmentIndex}`}
                                                sx={{
                                                  width: 55,
                                                  height: 55,
                                                  borderRadius: 1.5,
                                                  overflow: 'hidden',
                                                  cursor: 'pointer',
                                                  border: '1.5px solid',
                                                  borderColor: 'grey.200',
                                                  transition: 'all 0.2s ease-in-out',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  bgcolor: item.eventType === 'INSTRUCTION' ? '#fff' : (isImage ? 'transparent' : 'grey.50'),
                                                  p: '4px',
                                                  '&:hover': {
                                                    borderColor: 'primary.main',
                                                    transform: 'scale(1.05)',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                  }
                                                }}
                                              >
                                                {isSignedPdf ? (
                                                  <Box
                                                    sx={{ width: '100%', height: '100%' }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (img.url) window.open(img.url, '_blank', 'noopener');
                                                    }}
                                                  >
                                                    <PdfThumbnail url={img.url} size={55} />
                                                  </Box>
                                                ) : isImage || isVideo ? (
                                                  <img
                                                    src={isVideo ? video : img.url}
                                                    alt={img.file_name}
                                                    style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      objectFit: item.eventType === 'INSTRUCTION' ? 'contain' : 'cover',
                                                      display: 'block'
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation(); // Prevent triggering parent onClick
                                                      if (isVideo) {
                                                        setVideoUrl(img.url);
                                                        setShowVideo(true);
                                                      } else if (isImage) {
                                                        // Prepare images for attachments
                                                        const attachmentImages = item.files
                                                          .filter((file: any) => {
                                                            const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                          })
                                                          .map((file: any) => ({ src: file.url,question: file.file_name || file._doc?.file_name || file.file_name, }));

                                                        setImagesPreview(attachmentImages);
                                                        // Find the correct index in the filtered images array
                                                        const imageIndex = item.files
                                                          .filter((file: any) => {
                                                            const fileExt = file.file_name?.split('.').pop()?.toLowerCase() || '';
                                                            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
                                                          })
                                                          .findIndex((file: any) => file.url === img.url);

                                                        setCurrentImage(imageIndex >= 0 ? imageIndex : 0);
                                                        setViewerIsOpen(true);
                                                      }
                                                    }}
                                                  />
                                                ) : (
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      p: 0.5,
                                                      textAlign: 'center'
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      // For non-image files, open in new tab or download
                                                      window.open(img.url, '_blank');
                                                    }}
                                                  >
                                                    <InsertDriveFileIcon
                                                      sx={{
                                                        fontSize: 22,
                                                        color: 'primary.main',
                                                        mb: 0.5
                                                      }}
                                                    />
                                                    <Typography
                                                      sx={{
                                                        fontSize: '6px',
                                                        color: 'text.secondary',
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase'
                                                      }}
                                                    >
                                                      {ext || 'FILE'}
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  )}
                              </Grid>

                              {/* AI Detection Results - Collapsible. Only fully-evaluated
                                  results are shown (sourcesResolved !== false). Log-only
                                  rows stay in the DB as an audit trail but don't clutter
                                  the step history. */}
                              {item.detectionResults && item.detectionResults.length > 0 && (() => {
                                const visibleDetections = item.detectionResults.filter(
                                  (d: any) => d?.sourcesResolved !== false
                                );
                                if (visibleDetections.length === 0) return null;

                                const detKey = item._id || item.uid || `det-${idx}`;
                                // toJSON strips _id → the event exposes `id` (string _id) + uid.
                                // Use whichever is present so the retry action always has a target.
                                const detEventId = item._id || item.id || item.uid;
                                const isOpen = expandedDetections.has(detKey);

                                // Extract an optional `[Confidence: 92.5%]` prefix from aiMessage.
                                // Rekognition prepends this to its face-comparison response.
                                const splitConfidence = (msg?: string): { confidence: number | null; body: string } => {
                                  if (!msg) return { confidence: null, body: '' };
                                  const m = msg.match(/^\s*\[Confidence:\s*([\d.]+)%\]\s*(.*)$/is);
                                  if (!m) return { confidence: null, body: msg };
                                  const pct = parseFloat(m[1]);
                                  return { confidence: isNaN(pct) ? null : pct, body: m[2] };
                                };

                                // Strip **...** markers from an instruction string for a
                                // human-readable fallback when `name` is missing (old rows).
                                const instructionFallback = (instr?: string): string => {
                                  if (!instr) return 'Detection Rule';
                                  const stripped = instr.replace(/\*\*([^*]+)\*\*/g, '$1').trim();
                                  return stripped.length > 60 ? stripped.slice(0, 57) + '…' : stripped;
                                };

                                const relTime = (ts: any): string => {
                                  if (!ts) return '';
                                  const d = new Date(ts).getTime();
                                  if (isNaN(d)) return '';
                                  const diff = Math.max(0, Date.now() - d);
                                  const s = Math.floor(diff / 1000);
                                  if (s < 60) return `${s}s ago`;
                                  const m = Math.floor(s / 60);
                                  if (m < 60) return `${m}m ago`;
                                  const h = Math.floor(m / 60);
                                  if (h < 24) return `${h}h ago`;
                                  return new Date(ts).toLocaleDateString();
                                };

                                return (
                                  <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
                                    <Box
                                      onClick={() => {
                                        setExpandedDetections(prev => {
                                          const next = new Set(prev);
                                          if (next.has(detKey)) next.delete(detKey);
                                          else next.add(detKey);
                                          return next;
                                        });
                                      }}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', py: 0.5 }}
                                    >
                                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                        ▶
                                      </Typography>
                                      <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                        AI Detection ({visibleDetections.length})
                                      </Typography>
                                    </Box>
                                    {isOpen && visibleDetections.map((det: any, detIdx: number) => {
                                      // Read through detectionOutcome, never det.type — a manually
                                      // resolved result must render as resolved everywhere.
                                      const outcome = detectionOutcome(det);
                                      const resolved = isManuallyResolved(det);
                                      const severity = DETECTION_OUTCOME_SEVERITY[outcome] || 'info';
                                      const statusLabel = DETECTION_OUTCOME_LABEL[outcome] || 'Result';
                                      const displayName = (det?.name && String(det.name).trim()) || instructionFallback(det?.instruction);
                                      // Prefer the stored number; the string prefix is the
                                      // legacy carrier and stays only for older rows and
                                      // Rekognition, which still writes it that way.
                                      const parsed = splitConfidence(det?.aiMessage);
                                      const confidence = typeof det?.confidence === 'number' ? det.confidence : parsed.confidence;
                                      const body = parsed.body;
                                      const timestamp = relTime(det?.timestamp);
                                      const resolveKey = `${detEventId}:${det?.configId}`;
                                      const isResolving = resolvingDetection === resolveKey;

                                      return (
                                        <Alert
                                          key={detIdx}
                                          severity={severity}
                                          variant="outlined"
                                          icon={false}
                                          sx={{
                                            p: 1.25,
                                            mt: detIdx === 0 ? 1 : 0,
                                            mb: 0.75,
                                            borderRadius: 1.5,
                                            '& .MuiAlert-message': { width: '100%', p: 0 }
                                          }}
                                        >
                                          {/* Header: rule name + outcome chip + relative time */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, lineHeight: 1.3, color: 'text.primary' }}
                                            >
                                              {displayName}
                                            </Typography>
                                            <Chip
                                              label={statusLabel}
                                              size="small"
                                              color={severity}
                                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                            {resolved && (
                                              <Tooltip title="Resolved manually — the original result is shown below">
                                                <Chip
                                                  icon={<PanTool sx={{ fontSize: 12 }} />}
                                                  label="Manual"
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                                />
                                              </Tooltip>
                                            )}
                                            {/* An exact comparison and a model's judgement deserve
                                                different confidence from the reader. */}
                                            {det?.engine === 'deterministic' && (
                                              <Tooltip title="Evaluated by an exact comparison — no AI involved">
                                                <Chip
                                                  label="exact"
                                                  size="small"
                                                  variant="outlined"
                                                  color="success"
                                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                                />
                                              </Tooltip>
                                            )}
                                            {det?.doubleCheck && (
                                              <Tooltip
                                                title={
                                                  det.doubleCheck.agreed
                                                    ? 'Checked twice — both checks agreed'
                                                    : 'Checked twice — the checks disagreed, so a stronger model made the final call'
                                                }
                                              >
                                                <Chip
                                                  label="double-checked"
                                                  size="small"
                                                  variant="outlined"
                                                  color={det.doubleCheck.agreed ? 'info' : 'warning'}
                                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                                />
                                              </Tooltip>
                                            )}
                                            {timestamp && (
                                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                                                {timestamp}
                                              </Typography>
                                            )}
                                            {/* Resolve is available to any dashboard user, unlike retry —
                                                an inconclusive result sitting unresolved because nobody
                                                with the role is around helps no one. Only ever offered on
                                                an inconclusive result; the backend enforces the same. */}
                                            {detEventId && det?.configId && canManuallyResolve(det) && !resolved && (
                                              <Tooltip title="Resolve this inconclusive result">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    sx={{ p: 0.25 }}
                                                    disabled={isResolving}
                                                    onClick={() => setResolveDialog({
                                                      eventId: detEventId,
                                                      configId: det.configId,
                                                      name: displayName,
                                                      aiMessage: body,
                                                      outcome: 'false',
                                                      reason: '',
                                                    })}
                                                  >
                                                    {isResolving ? <CircularProgress size={13} /> : <PanTool sx={{ fontSize: 14 }} />}
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            )}
                                            {isSuperAdmin && detEventId && det?.configId && det?.engine !== 'jumio' && (
                                              <Tooltip title="Re-run this AI detection (Super Admin)">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    sx={{ p: 0.25 }}
                                                    disabled={retryingDetection === `${detEventId}:${det.configId}`}
                                                    onClick={() => setConfirmRetry({ eventId: detEventId, configId: det.configId, name: displayName })}
                                                  >
                                                    {retryingDetection === `${detEventId}:${det.configId}`
                                                      ? <CircularProgress size={13} />
                                                      : <Refresh sx={{ fontSize: 15 }} />}
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            )}
                                          </Box>

                                          {/* Confidence meter (Rekognition face comparison) */}
                                          {confidence !== null && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                <LinearProgress
                                                  variant="determinate"
                                                  value={Math.min(100, Math.max(0, confidence))}
                                                  sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    bgcolor: 'grey.200',
                                                    '& .MuiLinearProgress-bar': {
                                                      bgcolor: confidence >= 80 ? 'success.main' : 'error.main'
                                                    }
                                                  }}
                                                />
                                              </Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 700,
                                                  fontSize: '0.78rem',
                                                  color: confidence >= 80 ? 'success.main' : 'error.main',
                                                  minWidth: 48,
                                                  textAlign: 'right'
                                                }}
                                              >
                                                {confidence.toFixed(1)}%
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Main AI message — the thing the user actually wants to read */}
                                          {body && (
                                            <Typography
                                              variant="body2"
                                              sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.45 }}
                                            >
                                              {body}
                                            </Typography>
                                          )}

                                          {/* What the check actually observed. Deterministic rules
                                              carry the real computation here ("Distance 1.2 km vs
                                              limit 5 km"), which is what makes a disputed result
                                              defensible rather than just assertive. */}
                                          {Array.isArray(det?.evidence) && det.evidence.length > 0 && (
                                            <Box sx={{ mt: 0.75 }}>
                                              {det.evidence.map((ev: any, evIdx: number) => (
                                                <Box key={evIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                  <Typography sx={{ fontSize: '0.7rem', lineHeight: 1.6, color: ev?.supports === 'pass' ? 'success.main' : 'error.main' }}>
                                                    {ev?.supports === 'pass' ? '✓' : '✕'}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                                                    {ev?.observation}
                                                  </Typography>
                                                </Box>
                                              ))}
                                            </Box>
                                          )}

                                          {/* A disagreement is worth reading: both first answers, and
                                              which model broke the tie. Agreement needs no extra line. */}
                                          {det?.doubleCheck && !det.doubleCheck.agreed && (
                                            <Typography
                                              variant="caption"
                                              sx={{ display: 'block', mt: 0.75, fontSize: '0.7rem', color: 'warning.dark' }}
                                            >
                                              First two checks disagreed (
                                              {(det.doubleCheck.runs || [])
                                                .map((r: any) => DETECTION_OUTCOME_LABEL[r?.outcome as keyof typeof DETECTION_OUTCOME_LABEL] || r?.outcome)
                                                .join(' vs ')}
                                              ){det.doubleCheck.escalatedTo
                                                ? ` — ${formatModelName(det.doubleCheck.escalatedTo)} made the final call.`
                                                : ' — needs a human look.'}
                                            </Typography>
                                          )}

                                          {/* Error line */}
                                          {det?.error && (
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'error.main', display: 'block', mt: 0.5, fontSize: '0.7rem' }}
                                            >
                                              Error: {det.error}
                                            </Typography>
                                          )}

                                          {/* Manual resolution — shown alongside the engine's original
                                              answer above, never in place of it. Someone reading this
                                              months later needs to see both and who chose. */}
                                          {resolved && (
                                            <Box
                                              sx={{
                                                mt: 1,
                                                pt: 1,
                                                borderTop: '1px dashed',
                                                borderColor: 'divider',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                              }}
                                            >
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary' }}>
                                                  Resolved to <strong>{DETECTION_OUTCOME_LABEL[outcome]}</strong>
                                                  {det.manualOverride?.byName ? ` by ${det.manualOverride.byName}` : ''}
                                                  {det.manualOverride?.at ? ` · ${new Date(det.manualOverride.at).toLocaleString()}` : ''}
                                                </Typography>
                                                {det.manualOverride?.reason && (
                                                  <Typography variant="body2" sx={{ fontSize: '0.76rem', color: 'text.primary', mt: 0.25 }}>
                                                    “{det.manualOverride.reason}”
                                                  </Typography>
                                                )}
                                                {det.manualOverride?.history?.length > 0 && (
                                                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.66rem', color: 'text.disabled', mt: 0.25 }}>
                                                    {det.manualOverride.history.length} earlier{' '}
                                                    {det.manualOverride.history.length === 1 ? 'resolution' : 'resolutions'} on record
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Button
                                                size="small"
                                                variant="text"
                                                disabled={isResolving}
                                                onClick={() => undoResolveDetection(detEventId, det.configId)}
                                                sx={{ fontSize: '0.68rem', minWidth: 0, flexShrink: 0, textTransform: 'none' }}
                                              >
                                                {isResolving ? <CircularProgress size={12} /> : 'Undo'}
                                              </Button>
                                            </Box>
                                          )}
                                        </Alert>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}

                              {/* Footer Section with User, Location, and Date - Always Visible */}
                              <Box
                                sx={{
                                  borderTop: '1px solid',
                                  borderColor: 'grey.200',
                                  mt: 0,
                                  pt: 1.5,
                                  mx: 2,
                                  mb: 1,
                                  minHeight: '80px', // Ensure footer has minimum height
                                  display: 'none',
                                  alignItems: 'stretch',
                                
                                }}
                              >
                                <Grid container spacing={1.5} sx={{ alignItems: 'stretch' }}>
                                <Grid item xs={12} sm={item?.updatedBy ? 4 : 6}>
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '1.75rem',
                                color: 'text.secondary',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              
                            >
                              {/* <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}> */}
                              <Tooltip 
                                title={(() => {
                                  // Keep tooltip aligned with Created By details and include submitted time.
                                  const createdByName = (() => {
                                    if (item?.user?.firstName || item?.user?.lastName) {
                                      return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                    } else if (item?.user?.name) {
                                      return item.user.name;
                                    } else if (item?.userName) {
                                      return item.userName;
                                    } else if (item?.createdBy?.firstName || item?.createdBy?.lastName) {
                                      return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                    } else if (item?.createdBy?.name) {
                                      return item.createdBy.name;
                                    }
                                    return 'Not Available';
                                  })();

                                  const submittedTime = (() => {
                                    try {
                                      const dateValue = item?.updateTime || item?.createdAt;
                                      return dateValue ? formatTimestampWithLocalTimezone(dateValue) : 'Not Available';
                                    } catch (error) {
                                      return 'Not Available';
                                    }
                                  })();

                                  return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        {createdByName}
                                      </Typography>
                                      <Typography variant="caption">
                                        Submitted: {submittedTime}
                                      </Typography>
                                    </Box>
                                  );
                                })()}
                                arrow
                                placement="top"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 600,textTransform:"uppercase" }}>
                                    Created By:{' '}
                                  </Box>
                                  {(() => {
                                    // Handle different user data structures
                                    if (item?.user?.firstName || item?.user?.lastName) {
                                      return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                    } else if (item?.user?.name) {
                                      return item.user.name;
                                    } else if (item?.userName) {
                                      return item.userName;
                                    } else if (item?.createdBy?.firstName || item?.createdBy?.lastName) {
                                      return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                    } else if (item?.createdBy?.name) {
                                      return item.createdBy.name;
                                    } else {
                                    }
                                  })()}
                                </Typography>
                              </Tooltip>
                            
                            {/* </Box> */}
                              
                            </Typography>
                            
                            {(item?.user?.resourceTypeId?.roleName || item?.user?.resourceTypeId?.name || (typeof item?.user?.resourceTypeId === 'string' && item?.user?.resourceTypeId)) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.7rem',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  mt: 0.25
                                }}
                              >
                                ({item.user.resourceTypeId.name ? item.user.resourceTypeId.name : item.user.resourceTypeId.roleName})
                              </Typography>
                            )}
                            
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mt: 0.25
                              }}
                            >
                              {(() => {
                                // Handle different date field names and formats
                                try {
                                  const dateValue = item?.createdAt || item?.timestamp || item?.date || item?.created_at;
                                  if (dateValue) {
                                    return formatTimestampWithLocalTimezone(dateValue);
                                  }
                                  return 'Not Available';
                                } catch (error) {
                                  console.warn('Date parsing error:', error);
                                  return 'Not Available';
                                }
                              })()}
                            </Typography>
                          </Box>
                                  </Grid>

                                  <Grid item xs={12} sm={item?.updatedBy ? 4 : 6}>
                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      <Typography
                                        fontWeight={600}
                                        variant="caption"
                                        sx={{
                                          color: 'primary.main',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          fontSize: '0.65rem',
                                          mb: 0.5
                                        }}
                                      >
                                        Location
                                      </Typography>
                                      {(() => {
                                        // Handle different location data structures
                                        const hasValidCoordinates =
                                          item?.location?.coordinates &&
                                          Array.isArray(item.location.coordinates) &&
                                          item.location.coordinates.length >= 2 &&
                                          !isNaN(Number(item.location.coordinates[0])) &&
                                          !isNaN(Number(item.location.coordinates[1]));

                                        if (hasValidCoordinates) {
                                          return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                              <Box
                                                sx={{
                                                  height: 32,
                                                  width: 32,
                                                  borderRadius: 0.75,
                                                  border: '1px solid',
                                                  borderColor: 'grey.200',
                                                  bgcolor: 'grey.50',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  flexShrink: 0
                                                }}
                                              >
                                                <LocationOnIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                              </Box>
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'text.secondary',
                                                    lineHeight: 1.2,
                                                    mb: 0.25,
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  {item.location?.location || item.location?.address || 'Location Available'}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.65rem',
                                                    color: 'text.disabled',
                                                    lineHeight: 1,
                                                    fontFamily: 'monospace'
                                                  }}
                                                >
                                                  {`${Number(item.location.coordinates[0]).toFixed(4)}, ${Number(
                                                    item.location.coordinates[1]
                                                  ).toFixed(4)}`}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          );
                                        } else if (item?.location?.location || item?.location?.address || item?.locationText) {
                                          return (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                wordBreak: 'break-word'
                                              }}
                                            >
                                              {item.location?.location || item.location?.address || item.locationText}
                                            </Typography>
                                          );
                                        } else {
                                          return (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                fontStyle: 'italic'
                                              }}
                                            >
                                              Not Available
                                            </Typography>
                                          );
                                        }
                                      })()}
                                    </Box>
                                  </Grid>

                                  {/* Updated By Section - Only show if item has been updated */}
                                  {item?.updatedBy && item.updatedBy !== item?.user?.id && (
                                  <Grid item xs={12} sm={4}>
                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      <Tooltip 
                                        title={(() => {
                                          // If updatedBy is an object with user details
                                          if (typeof item.updatedBy === 'object' && item.updatedBy !== null) {
                                            if (item.updatedBy?.firstName || item.updatedBy?.lastName) {
                                              return `${item.updatedBy?.firstName || ''} ${item.updatedBy?.lastName || ''}`.trim();
                                            } else if (item.updatedBy?.name) {
                                              return item.updatedBy.name;
                                            }
                                          }
                                          
                                          // If updatedBy is just a user ID, try to find user info
                                          if (typeof item.updatedBy === 'string') {
                                            // Check if it's the same as current user
                                            if (item.updatedBy === item?.user?.id) {
                                              return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim() || item.user?.name || 'Current User';
                                            }
                                            // For now, just show "Updated" - in a real app, you'd fetch user details by ID
                                            return 'Updated User';
                                          }
                                          
                                          return 'Unknown User';
                                        })()}
                                        arrow
                                        placement="top"
                                      >
                                        <Typography
                                          variant="body2" 
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600,textTransform:"uppercase" }}>
                                            Last Updated By:{' '}
                                          </Box>
                                          {(() => {
                                            // If updatedBy is an object with user details
                                            if (typeof item.updatedBy === 'object' && item.updatedBy !== null) {
                                              if (item.updatedBy?.firstName || item.updatedBy?.lastName) {
                                                return `${item.updatedBy?.firstName || ''} ${item.updatedBy?.lastName || ''}`.trim();
                                              } else if (item.updatedBy?.name) {
                                                return item.updatedBy.name;
                                              }
                                            }
                                            
                                            // If updatedBy is just a user ID, try to find user info
                                            if (typeof item.updatedBy === 'string') {
                                              // Check if it's the same as current user
                                              if (item.updatedBy === item?.user?.id) {
                                                return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim() || item.user?.name || 'Current User';
                                              }
                                              // For now, just show "Updated" - in a real app, you'd fetch user details by ID
                                              return 'Updated User';
                                            }
                                            
                                            return 'Unknown User';
                                          })()}
                                        </Typography>
                                      </Tooltip>
                                      
                                      {(() => {
                                        // Show role for updatedBy if available
                                        if (typeof item.updatedBy === 'object' && item.updatedBy?.resourceTypeId?.name) {
                                          return (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.7rem',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                mt: 0.25
                                              }}
                                            >
                                              ({item.updatedBy.resourceTypeId.name})
                                            </Typography>
                                          );
                                        } else if (typeof item.updatedBy === 'string' && item.updatedBy === item?.user?.id && item.user?.resourceTypeId?.name) {
                                          return (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.7rem',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                mt: 0.25
                                              }}
                                            >
                                              ({item.user.resourceTypeId.name})
                                            </Typography>
                                          );
                                        }
                                        return null;
                                      })()}
                                      
                                      {item?.updatedAt && (
                                        <TimestampDisplay
                                          value={item.updatedAt}
                                          typographyProps={{
                                            variant: 'caption',
                                            sx: {
                                              fontSize: '0.7rem',
                                              color: 'text.secondary',
                                              fontWeight: 500,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              mt: 0.25,
                                              display: 'block'
                                            }
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                          </Grid>

                              </Box>
                            </Paper>
                              </Fragment>
                            );
                          });
                          return [
                            ...cards,
                            timelineLoadingMore ? (
                              <Box key="timeline-loading-more" sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : null
                          ];
                        })() : (
                          <Paper
                            elevation={0}
                            sx={{
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              p: 4,
                              textAlign: 'center',
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}
                          >
                            </Paper>
                        )}

                        {/* LOCATION events are now surfaced exclusively in the
                            consolidated <LiveDriverPanel> at the top of the
                            page (map + Start/End/NOW pins). Per-step LOCATION
                            cards are intentionally not rendered here to avoid
                            duplicating the same data. */}
                        {false && (() => {
                          const locationEvents = transactionDetails.history
                            .filter((item: any) => item.eventType === 'LOCATION');
                          if (locationEvents.length === 0) return null;

                          // Build a deduplicated participants roster so the
                          // consolidated map can label each driver's track. Each
                          // LOCATION event carries the submitting user under
                          // `item.user`; we coalesce by uid and prefer the most
                          // complete display name we can derive.
                          const participantMap = new Map<string, { uid: string; name: string }>();
                          locationEvents.forEach((item: any) => {
                            const uid = item?.user?.uid;
                            if (!uid) return;
                            const name =
                              [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(' ').trim() ||
                              item?.user?.name ||
                              item?.userName ||
                              '';
                            if (!participantMap.has(uid) || (name && !participantMap.get(uid)?.name)) {
                              participantMap.set(uid, { uid, name });
                            }
                          });
                          const participants = Array.from(participantMap.values());

                          return (
                            <>
                              {/* Single consolidated tracking map: one canvas, every driver. */}
                              {transactionDetails?.uid && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    background: 'white',
                                    borderRadius: '10px',
                                    marginBottom: '10px',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: '10px',
                                      backgroundColor: 'primary.dark',
                                      borderTopLeftRadius: 6,
                                      borderTopRightRadius: 6,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    <Typography
                                      fontWeight={600}
                                      variant="subtitle1"
                                      sx={{ color: '#fff', fontSize: '12px', letterSpacing: '0.025em' }}
                                    >
                                      Live Tracking
                                    </Typography>
                                  </Box>
                                  <LiveTrackingMap
                                    txnId={transactionDetails.uid}
                                    participants={participants}
                                    pickup={transactionDetails.pickupAddress}
                                    destination={transactionDetails.destinationAddress}
                                  />
                                </Paper>
                              )}

                              {locationEvents.slice().reverse().map((item: any, idx: number) => (
                              <Paper
                                elevation={0}
                                sx={{
                                  background: 'white',
                                  borderRadius: "10px",
                                  marginBottom: "10px",
                                  border: '1px solid',
                                  borderColor: 'grey.200',
                                  transition: 'all 0.2s ease-in-out',
                                  overflow: 'hidden',
                                  scrollMarginTop: 120
                                }}
                                id={item?.step?.uid ? `step-${item.step.uid}` : undefined}
                                key={`transaction-item-${item._id || item.step?.title || idx}-${idx}`}
                                onClick={() => { getHistoryData(item); }}
                              >
                                {item.step?.title && (
                                  <Box
                                    sx={{
                                      p: "10px",
                                      backgroundColor: 'primary.dark',
                                      borderTopLeftRadius: 6,
                                      borderTopRightRadius: 6,
                                      position: 'relative',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      paddingLeft: "10px"
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography
                                        fontWeight={600}
                                        variant="subtitle1"
                                        sx={{ color: '#fff', fontSize: '12px', letterSpacing: '0.025em' }}
                                      >
                                        {item.step?.title}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'grey.200',
                                    mt: 2,
                                    pt: 1.5,
                                    mx: 2,
                                    mb: 1,
                                    minHeight: '80px', 
                                    display: 'flex',
                                    alignItems: 'stretch'
                                  }}
                                >
                                  <Grid container spacing={1.5} sx={{ alignItems: 'stretch' }}>
                                    <Grid item xs={12} sm={item?.updatedBy ? 4 : 6}>
                                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                          fontWeight={600}
                                          variant="caption"
                                          sx={{
                                            color: 'primary.main',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.65rem',
                                            mb: 0.5
                                          }}
                                        >
                                          Created By
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {(() => {
                                            if (item?.user?.firstName || item?.user?.lastName) {
                                              return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
                                            } else if (item?.user?.name) {
                                              return item.user.name;
                                            } else if (item?.userName) {
                                              return item.userName;
                                            } else if (item?.createdBy?.firstName || item?.createdBy?.lastName) {
                                              return `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
                                            } else if (item?.createdBy?.name) {
                                              return item.createdBy.name;
                                            } else {
                                              return 'Not Available';
                                            }
                                          })()}
                                        </Typography>
                                        {(item?.user?.resourceTypeId?.roleName || item?.user?.resourceTypeId?.name || (typeof item?.user?.resourceTypeId === 'string' && item?.user?.resourceTypeId)) && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: '0.7rem',
                                              color: 'text.secondary',
                                              fontWeight: 500,
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            ({item.user.resourceTypeId.name ? item.user.resourceTypeId.name : item.user.resourceTypeId.roleName})
                                          </Typography>
                                        )}
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.7rem',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            wordBreak: 'break-word',
                                            mt: 0.25
                                          }}
                                        >
                                          {(() => {
                                            // Handle different date field names and formats
                                            try {
                                              const dateValue = item?.createdAt || item?.timestamp || item?.date || item?.created_at;
                                              if (dateValue) {
                                                return formatTimestampWithLocalTimezone(dateValue);
                                              }
                                              return 'Not Available';
                                            } catch (error) {
                                              console.warn('Date parsing error:', error);
                                              return 'Not Available';
                                            }
                                          })()}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={item?.updatedBy ? 4 : 6}>
                                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                          fontWeight={600}
                                          variant="caption"
                                          sx={{
                                            color: 'primary.main',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.65rem',
                                            mb: 0.5
                                          }}
                                        >
                                          Location
                                        </Typography>
                                        {(() => {
                                          const hasValidCoordinates =
                                            item?.location?.coordinates &&
                                            Array.isArray(item.location.coordinates) &&
                                            item.location.coordinates.length >= 2 &&
                                            !isNaN(Number(item.location.coordinates[0])) &&
                                            !isNaN(Number(item.location.coordinates[1]));

                                          if (hasValidCoordinates) {
                                            return (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Box
                                                  sx={{
                                                    height: 32,
                                                    width: 32,
                                                    borderRadius: 0.75,
                                                    border: '1px solid',
                                                    borderColor: 'grey.200',
                                                    bgcolor: 'grey.50',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                  }}
                                                >
                                                  <LocationOnIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontSize: '0.7rem',
                                                      color: 'text.secondary',
                                                      lineHeight: 1.2,
                                                      mb: 0.25,
                                                      wordBreak: 'break-word'
                                                    }}
                                                  >
                                                    {item.location?.location || item.location?.address || 'Location Available'}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontSize: '0.65rem',
                                                      color: 'text.disabled',
                                                      lineHeight: 1,
                                                      fontFamily: 'monospace'
                                                    }}
                                                  >
                                                    {`${Number(item.location.coordinates[0]).toFixed(4)}, ${Number(
                                                      item.location.coordinates[1]
                                                    ).toFixed(4)}`}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            );
                                          } else if (item?.location?.location || item?.location?.address || item?.locationText) {
                                            return (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  color: 'text.secondary',
                                                  fontWeight: 500,
                                                  wordBreak: 'break-word'
                                                }}
                                              >
                                                {item.location?.location || item.location?.address || item.locationText}
                                              </Typography>
                                            );
                                          } else {
                                            return (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  color: 'text.secondary',
                                                  fontWeight: 500,
                                                  fontStyle: 'italic'
                                                }}
                                              >
                                                Not Available
                                              </Typography>
                                            );
                                          }
                                        })()}
                                      </Box>
                                    </Grid>

                                    {/* Updated By Section - Only show if item has been updated */}
                                    {item?.updatedBy && item.updatedBy !== item?.user?.id && (
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                          <Typography
                                            fontWeight={600}
                                            variant="caption"
                                            sx={{
                                              color: 'primary.main',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.05em',
                                              fontSize: '0.65rem',
                                              mb: 0.5
                                            }}
                                          >
                                            Last Updated By
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '0.75rem',
                                              color: 'text.secondary',
                                              fontWeight: 500,
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {(() => {
                                              // If updatedBy is an object with user details
                                              if (typeof item.updatedBy === 'object' && item.updatedBy !== null) {
                                                if (item.updatedBy?.firstName || item.updatedBy?.lastName) {
                                                  return `${item.updatedBy?.firstName || ''} ${item.updatedBy?.lastName || ''}`.trim();
                                                } else if (item.updatedBy?.name) {
                                                  return item.updatedBy.name;
                                                }
                                              }
                                              
                                              // If updatedBy is just a user ID, try to find user info
                                              if (typeof item.updatedBy === 'string') {
                                                // Check if it's the same as current user
                                                if (item.updatedBy === item?.user?.id) {
                                                  return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim() || item.user?.name || 'Current User';
                                                }
                                                // For now, just show "Updated" - in a real app, you'd fetch user details by ID
                                                return 'Updated User';
                                              }
                                              
                                              return 'Unknown User';
                                            })()}
                                          </Typography>
                                          {(() => {
                                            // Show role for updatedBy if available
                                            if (typeof item.updatedBy === 'object' && item.updatedBy?.resourceTypeId?.name) {
                                              return (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'text.secondary',
                                                    fontWeight: 500,
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  ({item.updatedBy.resourceTypeId.name})
                                                </Typography>
                                              );
                                            } else if (typeof item.updatedBy === 'string' && item.updatedBy === item?.user?.id && item.user?.resourceTypeId?.name) {
                                              return (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'text.secondary',
                                                    fontWeight: 500,
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  ({item.user.resourceTypeId.name})
                                                </Typography>
                                              );
                                            }
                                            return null;
                                          })()}
                                          {item?.updatedAt && (
                                            <TimestampDisplay
                                              value={item.updatedAt}
                                              typographyProps={{
                                                variant: 'caption',
                                                sx: {
                                                  fontSize: '0.7rem',
                                                  color: 'text.secondary',
                                                  fontWeight: 500,
                                                  wordBreak: 'break-word',
                                                  mt: 0.25,
                                                  display: 'block'
                                                }
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </Paper>
                              ))}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  </>
                ) : transUid ? (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      fontSize: 15
                    }}
                  >
                    No Transaction Details History Currently Available
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12} md={5} >
              {loading ? (
                // Core tier hasn't resolved yet, so hasFormFields is still
                // unknown — without this, the ternary below falls through to
                // "no form fields" before we actually know that's true.
                <Box
                  sx={{
                    minHeight: 220,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading next step…
                  </Typography>
                </Box>
              ) : formRefreshLoading ? (
                <Box
                  sx={{
                    minHeight: 220,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading next form data...
                  </Typography>
                </Box>
              ) : editingStepId && editingStepItem ? (
                <EditForm
                  stepItem={editingStepItem}
                  onSave={handleSaveStep}
                  onCancel={handleCancelEdit}
                  saving={saving}
                  transactionHistory={transactionDetails?.history || []}
                />
              ) : transactionDetails?.childTxn?.enabled ? (
                // ✅ Inline, no dialog
                <InlineChildTransactionForm 
                  transactionUid={transUid!}
                  transactionKey={transactionDetails?.key}
                  teamUid={transactionDetails?.childTxn?.team?.uid}
                  childData={transactionDetails?.childTxn?.childData}
                  onSuccess={() => {
                    void refreshAfterFormSubmit();
                  }}
                />
              ) : transUid && hasFormFields ? (
                <FormData
                  transactionDetails={
                    transactionDetails?.step?.component?.fields?.length
                      ? transactionDetails.step
                      : transactionDetails?.component?.fields?.length
                      ? transactionDetails
                      : transactionDetails?.step?.stepType === 'INSTRUCTION'
                      ? transactionDetails.step
                      : transactionDetails?.step?.component?.componentType === 'INSTRUCTION'
                      ? transactionDetails.step
                      : transactionDetails // fallback to full transactionDetails
                  }
                  loadTransactionDetails={refreshAfterNewEvent}
                  FullTransaction={transactionDetails}
                  setTransactionDetails={setTransactionDetails}
                  onSuccess={onSuccess}
                />
              ) : transUid && !hasFormFields ? (
                <Box
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    fontSize: 15
                  }}
                >
                  No next steps or form fields available for this transaction.
                </Box>
              ) : (
                !transUid && canEditTransaction && <AddTransactionPage />
              )}
          
         
            </Grid>
          </Grid>
          )}
        </Box>
        
      </MainCard>

      {/* Transaction Embed Dialog */}
      {/* <Dialog
        open={canViewTransactionEmbed && showEmbedSection}
        onClose={() => setShowEmbedSection(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, bgcolor: 'primary.main', color: 'white', flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Transaction Report — {transactionDetails?.key}
          </Typography>
          <IconButton size="small" onClick={() => setShowEmbedSection(false)} sx={{ color: 'white' }}>
            <HighlightOffIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {embedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={36} />
            </Box>
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              frameBorder={0}
              width="100%"
              height="100%"
              allowTransparency
              style={{ border: 'none', display: 'block', flex: 1, minHeight: 0 }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Unable to load report. Please try again.</Typography>
            </Box>
          )}
        </Box>
      </Dialog> */}

      {/* Single Video Dialog for all video playback */}
      {showVideo && (
        <Dialog
          open={showVideo}
          onClose={() => setShowVideo(false)}
          maxWidth="xl"
          PaperProps={{
            sx: {
              width: 'fit-content',
              maxWidth: '100%',
              m: 2
            }
          }}
        >
          <Box position="relative">
            <IconButton onClick={() => setShowVideo(false)} sx={{ position: 'absolute', top: 8, right: 8, color: 'red', zIndex: 1 }}>
              <HighlightOffIcon fontSize="large" />
            </IconButton>
            <video
              src={videoUrl}
              controls
              autoPlay
              style={{
                width: 'auto',
                maxWidth: '90vw',
                maxHeight: '80vh',
                height: 'auto',
                display: 'block'
              }}
            />
          </Box>
        </Dialog>
      )}

      <GlobalStyles
        styles={{
          '#viewerBackdrop figure img': {
            backgroundColor: '#fff',
            transform: `scale(${zoomLevel}) translate(${pan.x / zoomLevel}px, ${pan.y / zoomLevel}px)`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            transformOrigin: 'center center',
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            // Critical: don't block mousedown from reaching the backdrop
            pointerEvents: zoomLevel > 1 ? 'none' : 'auto',  // ← ADD THIS
          },
          '#viewerBackdrop figure': {
            overflow: 'visible',
          },
          // Make backdrop itself catch all events
          '#viewerBackdrop': {
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          },
        }}
      />
    
      {viewerIsOpen && (
            <Box
              id="viewerBackdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget && !hasDraggedRef.current) closeViewer();
              }}
              sx={{
                position: 'fixed', inset: 0, zIndex: 9999,
                bgcolor: 'rgba(0,0,0,0.5)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
             <Box sx={{
                  width: isMaximized ? '100vw' : '90vw',
                  maxWidth: isMaximized ? '100vw' : 900,
                  height: isMaximized ? '100vh' : 'auto',
                  maxHeight: isMaximized ? '100vh' : 'auto',
                  bgcolor: 'rgba(20,20,20,0.95)',
                  borderRadius: isMaximized ? 0 : 2,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden',
                  border: isMaximized ? 'none' : '0.5px solid rgba(255,255,255,0.12)',
                  transition: 'all 0.2s ease-in-out',
                }}>
                {/* ── Header bar ── */}
                {/* ── Header bar ── (only counter + close now) */}
                <Box sx={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5, py: 1,
                  borderBottom: '0.5px solid rgba(255,255,255,0.1)',
                  flexShrink: 0,
                }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.04em' }}>
                    {currentImage + 1} / {imagesPreview.length}
                  </Typography>

                   <Typography sx={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: '12px',
                      fontWeight: 600,
                      textAlign: 'center',
                      letterSpacing: '0.02em',
                      flex: 1,
                      mx: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {imagesPreview[currentImage]?.question || ''}
                    </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Maximize toggle */}
                    <IconButton size="small" onClick={() => setIsMaximized(prev => !prev)}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 1, width: 28, height: 28 }}
                    >
                      {isMaximized
                        ? <FullscreenExit sx={{ fontSize: 16 }} />
                        : <Fullscreen sx={{ fontSize: 16 }} />
                      }
                    </IconButton>

                    {/* Close button */}
                    <IconButton size="small" onClick={closeViewer}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.18)', borderRadius: 1, width: 28, height: 28 }}
                    >
                      <HighlightOffIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
                {/* ── Image area ── */}
                      
                <Box sx={{
                    flex: 1, minHeight: isMaximized ? 0 : '60vh',
                    maxHeight: isMaximized ? 'none' : '80vh',
                    overflow: 'hidden', position: 'relative',
                    display: 'flex', flexDirection: 'column',  // ← change to column
                    alignItems: 'center', justifyContent: 'center',
                    cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  }}>
                    {imagesPreview[currentImage] && (
                      <>
                        <Box component="figure" sx={{ m: 0, overflow: 'visible', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img
                            src={imagesPreview[currentImage].src}
                            alt={`Image ${currentImage + 1}`}
                            style={{
                              maxWidth: '85vw', maxHeight: isMaximized ? '82vh' : '68vh',
                              objectFit: 'contain', display: 'block',
                              backgroundColor: '#fff',
                              transform: `scale(${zoomLevel}) translate(${pan.x / zoomLevel}px, ${pan.y / zoomLevel}px)`,
                              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                              transformOrigin: 'center center',
                              userSelect: 'none',
                              pointerEvents: zoomLevel > 1 ? 'none' : 'auto',
                            }}
                          />
                        </Box>
                      </>
                    )}

                    {/* Prev / Next arrows */}
                    {imagesPreview.length > 1 && (
                      <>
                        <IconButton onClick={gotoPrev}
                          sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                          <ArrowBack />
                        </IconButton>
                        <IconButton onClick={gotoNext}
                          sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                          <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                   {/* ── Footer: dot indicators centered + zoom controls right ── */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 1,
                    borderTop: '0.5px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                  }}>

                    {/* Left spacer — same width as zoom controls to keep dots truly centered */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, visibility: 'hidden' }}>
                      <Box sx={{ width: 28 }} />
                      <Box sx={{ width: 90 }} />
                      <Box sx={{ width: 28 }} />
                      <Box sx={{ width: 32 }} />
                    </Box>

                    {/* Dot indicators — center */}
                    {imagesPreview.length > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {imagesPreview.map((_: any, i: number) => (
                          <Box
                            key={i}
                            onClick={() => {
                              setCurrentImage(i);
                              setZoomLevel(1); zoomLevelRef.current = 1;
                              setPan({ x: 0, y: 0 }); panRef.current = { x: 0, y: 0 };
                            }}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}
                          >
                            {i === currentImage ? (
                              <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                bgcolor: '#1976d2', borderRadius: '10px', px: 1, py: 0.25,
                                border: '1px solid rgba(25,118,210,0.6)',
                              }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0 }} />
                                <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 600, lineHeight: 1 }}>
                                  {i + 1}
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{
                                width: 6, height: 6, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.3)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
                              }} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Zoom controls — right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small"
                        onClick={() => {
                          const next = Math.max(1, zoomLevel - 0.5);
                          setZoomLevel(next); zoomLevelRef.current = next;
                          if (next <= 1) { panRef.current = { x: 0, y: 0 }; setPan({ x: 0, y: 0 }); }
                        }}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 1, width: 28, height: 28, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                      >
                        <ZoomOut sx={{ fontSize: 16 }} />
                      </IconButton>

                      <Slider value={zoomLevel}
                        onChange={(_, val) => {
                          const z = val as number;
                          setZoomLevel(z); zoomLevelRef.current = z;
                          if (z <= 1) { panRef.current = { x: 0, y: 0 }; setPan({ x: 0, y: 0 }); }
                        }}
                        min={1} max={4} step={0.1}
                        sx={{ width: 90, color: '#1976d2', '& .MuiSlider-thumb': { width: 12, height: 12 }, '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                      />

                      <IconButton size="small"
                        onClick={() => {
                          const next = Math.min(4, zoomLevel + 0.5);
                          setZoomLevel(next); zoomLevelRef.current = next;
                        }}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 1, width: 28, height: 28, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                      >
                        <ZoomIn sx={{ fontSize: 16 }} />
                      </IconButton>

                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 500, minWidth: 32, textAlign: 'right' }}>
                        {zoomLevel.toFixed(1)}x
                      </Typography>
                    </Box>

                  </Box>
              </Box>
            </Box>
          )}

      {/* View Resource Dialog */}
      <ResourceDetailsDialog
        open={resourceDialogOpen}
        onClose={handleResourceDialogClose}
        resource={resourceDialogData}
        currentTransactionId={transUid}
        allowRating={true}
        userId={user?.uid || user?.id || ''}
      />

      {/* Retry AI Detection Confirmation Dialog */}
      <Dialog open={!!confirmRetry} onClose={() => setConfirmRetry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Re-run this AI detection?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            This will re-run <strong>{confirmRetry?.name}</strong> and <strong>permanently replace</strong> its
            previous result. This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRetry(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={() => {
              const c = confirmRetry;
              setConfirmRetry(null);
              if (c) runRetryDetection(c.eventId, c.configId);
            }}
          >
            Re-run detection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve an inconclusive detection result. The reason is mandatory —
          it is the entire audit value of the override. */}
      <Dialog
        open={!!resolveDialog}
        onClose={() => !resolvingDetection && setResolveDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Resolve this result</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            <strong>{resolveDialog?.name}</strong> came back inconclusive. Recording an outcome here
            does not change what the check found — both are kept.
          </Typography>

          {resolveDialog?.aiMessage && (
            <Box sx={{ mb: 2, p: 1.25, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                The check reported
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                {resolveDialog.aiMessage}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Outcome</InputLabel>
            <Select
              label="Outcome"
              value={resolveDialog?.outcome ?? 'false'}
              onChange={(e) =>
                setResolveDialog((d) => (d ? { ...d, outcome: e.target.value as 'true' | 'false' } : d))
              }
            >
              <MenuItem value="false">No Match</MenuItem>
              <MenuItem value="true">Match</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            required
            label="Reason"
            placeholder="What did you check, and what did you find?"
            value={resolveDialog?.reason ?? ''}
            onChange={(e) => setResolveDialog((d) => (d ? { ...d, reason: e.target.value } : d))}
            helperText="Recorded against the result, with your name and the time."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveDialog(null)} disabled={!!resolvingDetection}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!resolveDialog?.reason?.trim() || !!resolvingDetection}
            onClick={submitResolveDetection}
          >
            {resolvingDetection ? 'Saving…' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redo Confirmation Dialog */}
      <Dialog
        open={redoConfirmOpen}
        onClose={() => !saving && setRedoConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Reset Step</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            Step: <strong>{stepToRedo?.step?.title || stepToRedo?.stepTitle || '—'}</strong>
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 2, '&.Mui-focused': { color: 'text.primary' } }}>
              Choose reset type
            </FormLabel>
            <RadioGroup value={redoType} onChange={(e) => setRedoType(e.target.value as any)}>
              {/* Option 1 */}
              <Box sx={{ border: '1px solid', borderColor: redoType === 'current' ? 'primary.main' : 'divider', borderRadius: 1.5, px: 1.5, py: 0.5, mb: 1, bgcolor: redoType === 'current' ? 'primary.50' : 'transparent' }}>
                <FormControlLabel
                  value="current"
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Reset current step only</Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0, alignItems: 'center' }}
                />
              </Box>

              {/* Option 2 */}
              <Box sx={{ border: '1px solid', borderColor: redoType === 'full' ? 'error.main' : 'divider', borderRadius: 1.5, px: 1.5, py: 0.5, mb: 1, bgcolor: redoType === 'full' ? 'error.50' : 'transparent' }}>
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" color="error" />}
                  label={
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Reset this step and following steps</Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0, alignItems: 'center' }}
                />
              </Box>

              {/* Option 3 */}
              <Box sx={{ border: '1px solid', borderColor: redoType === 'role' ? 'warning.main' : 'divider', borderRadius: 1.5, px: 1.5, py: 0.5, mb: redoType === 'role' ? 0 : 0, bgcolor: redoType === 'role' ? 'warning.50' : 'transparent' }}>
                <FormControlLabel
                  value="role"
                  control={<Radio size="small" color="warning" />}
                  label={
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Reset this and following steps for a role</Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0, alignItems: 'center' }}
                />
                {redoType === 'role' && (
                  <Box sx={{ mt: 1.5, mb: 1, pl: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="role-select-label">Select Role</InputLabel>
                      <Select
                        labelId="role-select-label"
                        value={selectedRoleUid}
                        label="Select Role"
                        onChange={(e) => setSelectedRoleUid(e.target.value)}
                        disabled={rolesLoading}
                      >
                        {rolesLoading ? (
                          <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading roles...</MenuItem>
                        ) : availableRoles.length === 0 ? (
                          <MenuItem disabled>No roles found</MenuItem>
                        ) : (
                          availableRoles.map((role: any) => (
                            <MenuItem key={role.uid || role._id} value={role.uid}>
                              {role.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </RadioGroup>
          </FormControl>

          {redoType === 'full' && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500 }}>
                This will mark all events from this step onwards as reset and restore currentSteps to the state captured at this step.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setRedoConfirmOpen(false)} variant="outlined" disabled={saving} sx={{ minWidth: 90 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedRedo}
            variant="contained"
            color={redoType === 'full' ? 'error' : redoType === 'role' ? 'warning' : 'primary'}
            disabled={saving || (redoType === 'role' && !selectedRoleUid)}
            sx={{ minWidth: 120 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Confirm Reset'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <MissedCallDetailsDrawer
        open={open}
        onClose={toggleDrawer(false)}
        history={missedCallHistory}
        formatToLocalTime={formatToLocalTime}
        video={video}
        handlePlayVideo={handlePlayVideo}
        handleImage={handleImage}
        transactionId={transUid}
        transactionKey={transactionDetails?.transactionKey || transactionDetails?.key}
        // Quick Chat: same shared `assignedUsers` memo Quick Call filters the call
        // dialog down to, so starting a chat from the Communication panel lands on
        // the users on this transaction — assigned on a step, or having submitted
        // one. With neither present the restriction is dropped and the team shows.
        assignedUserUids={assignedUsers.callableUids}
      />

      {/* <ChildTransactionsDialog
        open={openChildDialog}
        onClose={handleCloseChildDialog}
        teamUid={transactionDetails?.childTxn?.team?.uid}
        transactionUid={transUid}
        transactionKey={transactionDetails?.transactionKey || transactionDetails?.key}
        childData={transactionDetails?.childTxn?.childData}
      /> */}

      <ChildDetailsDialog
        open={childDialogOpen}
        onClose={() => setChildDialogOpen(false)}
        child={selectedChild}
      />

    </Box>
  );
}
